/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Sklls API
 * OpenAPI spec version: v1
 */
export type AccessLevel = (typeof AccessLevel)[keyof typeof AccessLevel];

export const AccessLevel = {
  CanRead: 'CanRead',
  CanEdit: 'CanEdit',
  CanAdministrate: 'CanAdministrate'
} as const;

/**
 * A running VITAL case as seen by a client that wants to connect to it. Projects just what a
 * joining ambulance/ED screen needs: the case id, the patient session to drive/observe, and which
 * role is currently active.
 */
export interface ActiveCaseDto {
  simulationId?: number;
  caseId: string;
  /** @nullable */
  title: string | null;
  /** @nullable */
  startedDateTime?: string | null;
  /**
   * The single patient session, shared across the whole journey.
   * @nullable
   */
  patientSessionId?: string | null;
  /**
   * Role key of the currently-active stage (e.g. "operator"/"paramedic"/"doctor"), or null.
   * @nullable
   */
  activeStageRole?: string | null;
  /** @nullable */
  activeStageSortOrder?: number | null;
  /** True while an active stage exists (a role is currently driving the patient conversation). */
  patientConversationOpen?: boolean;
  /**
   * True once an ambulance has been dispatched for this case, so a joining ambulance/ED client can
   * render transport mode after a refresh and the AMK client won't offer to dispatch again.
   */
  ambulanceDispatched?: boolean;
  /**
   * When the ambulance was dispatched, or null if not yet dispatched.
   * @nullable
   */
  ambulanceDispatchedDateTime?: string | null;
  /**
   * The dispatched unit identifier, if one was supplied.
   * @nullable
   */
  ambulanceUnit?: string | null;
  /**
   * The dispatch ETA in seconds, if one was supplied.
   * @nullable
   */
  ambulanceEtaSeconds?: number | null;
  /**
   * True once the ambulance has arrived at the patient this run. Persisted server-side because
   * the arrival event has no replay — this is what re-enables the takeover gate after a reload,
   * late join or socket drop.
   */
  ambulanceArrived?: boolean;
}

export type BlockType = (typeof BlockType)[keyof typeof BlockType];

export const BlockType = {
  Normal: 'Normal',
  Start: 'Start',
  End: 'End',
  Service: 'Service'
} as const;

export type QualityLevel = (typeof QualityLevel)[keyof typeof QualityLevel];

export const QualityLevel = {
  Unset: 'Unset',
  Bad: 'Bad',
  Acceptable: 'Acceptable',
  Good: 'Good'
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserRole = {
  Member: 'Member',
  Admin: 'Admin',
  Owner: 'Owner',
  SuperAdmin: 'SuperAdmin'
} as const;

export interface LicenseAgreement {
  id?: number;
  companyId?: number;
  company?: Company;
  /** @nullable */
  departmentId?: number | null;
  department?: CompanyDepartment;
  skllsSeats?: number;
  interpreterSeats?: number;
  exercisesSeats?: number;
  recordingsSeats?: number;
  startTime?: string;
  expiryTime?: string;
}

export interface CompanyDepartment {
  id?: number;
  /** @nullable */
  name: string | null;
  companyId?: number;
  company?: Company;
  /** @nullable */
  licenseAgreements?: LicenseAgreement[] | null;
}

export type EvaluationProtocolElementType =
  (typeof EvaluationProtocolElementType)[keyof typeof EvaluationProtocolElementType];

export const EvaluationProtocolElementType = {
  Numeric: 'Numeric',
  FreeText: 'FreeText',
  MultipleChoice: 'MultipleChoice',
  YesNo: 'YesNo',
  ElapsedTime: 'ElapsedTime'
} as const;

export interface EvaluationProtocolElement {
  id?: number;
  evaluationProtocolCategoryId?: number;
  order?: number;
  /** @nullable */
  name?: string | null;
  type?: EvaluationProtocolElementType;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  /** @nullable */
  choices?: string | null;
  explanationEnabled?: boolean;
  allowNotApplicable?: boolean;
  allowMultiple?: boolean;
  /** @nullable */
  goalSeconds?: number | null;
  excludedFromScore?: boolean;
  /** @nullable */
  description: string | null;
  /** @nullable */
  currentVersionId?: number | null;
}

export type VoiceResponseUsecase = (typeof VoiceResponseUsecase)[keyof typeof VoiceResponseUsecase];

export const VoiceResponseUsecase = {
  Success: 'Success',
  Fail: 'Fail'
} as const;

export interface AiVoiceResponse {
  id?: number;
  /** @nullable */
  text: string | null;
  voiceResponseUsecase?: VoiceResponseUsecase;
}

export interface IntentPhrase {
  id?: number;
  /** @nullable */
  text: string | null;
}

export type PersonaUsage = (typeof PersonaUsage)[keyof typeof PersonaUsage];

export const PersonaUsage = {
  Default: 'Default',
  Intro: 'Intro',
  Fallback: 'Fallback',
  AgonalBreathing: 'AgonalBreathing'
} as const;

export interface ScenarioPersona {
  id?: number;
  scenarioId?: number;
  personaId?: number;
  /** @nullable */
  personaUsages?: PersonaUsage[] | null;
  /** @nullable */
  roleId?: number | null;
}

export interface Block {
  id?: number;
  /** @nullable */
  name: string | null;
  type?: BlockType;
  /** @nullable */
  description?: string | null;
  qualityLevel?: QualityLevel;
  /** @nullable */
  globalAssetIntents?: GlobalAssetIntent[] | null;
  /** @nullable */
  aiResponses?: AiVoiceResponse[] | null;
  /** @nullable */
  intentPhrases?: IntentPhrase[] | null;
  posX?: number;
  posY?: number;
  scenario?: Scenario;
  scenarioId?: number;
  scores?: number;
  /** @nullable */
  frontendBlockId?: string | null;
  /** @nullable */
  scenarioPersonaId?: number | null;
  scenarioPersona?: ScenarioPersona;
}

export interface BlockLink {
  id?: number;
  origin?: Block;
  originId?: number;
  target?: Block;
  targetId?: number;
  scenario?: Scenario;
  scenarioId?: number;
}

export interface ScheduledCourseUserGroup {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  scheduledCourseId?: number;
  scheduledCourse: ScheduledCourse;
  userGroupId?: number;
  userGroup: UserGroup;
}

export interface UserGroup {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  /** @nullable */
  title: string | null;
  company?: Company;
  companyId?: number;
  /** @nullable */
  departmentId?: number | null;
  department?: CompanyDepartment;
  /** @nullable */
  users?: User[] | null;
  /** @nullable */
  scheduledCourses?: ScheduledCourse[] | null;
  /** @nullable */
  scheduledCourseUserGroups?: ScheduledCourseUserGroup[] | null;
}

export interface ScheduledCourseUser {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  scheduledCourseId?: number;
  scheduledCourse: ScheduledCourse;
  userId?: number;
  user: User;
}

export interface ScheduledCourseNotification {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  scheduledCourseId?: number;
  scheduledCourse: ScheduledCourse;
  date?: string;
  /** @nullable */
  customText: string | null;
}

export interface ScheduledCourse {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  courseId?: number;
  /** @nullable */
  departmentId?: number | null;
  course: Course;
  department?: CompanyDepartment;
  startDate?: string;
  endDate?: string;
  /** @nullable */
  users?: User[] | null;
  /** @nullable */
  userGroups?: UserGroup[] | null;
  /** @nullable */
  scheduledCourseUsers?: ScheduledCourseUser[] | null;
  /** @nullable */
  scheduledCourseUserGroups?: ScheduledCourseUserGroup[] | null;
  /** @nullable */
  scheduledCourseNotifications?: ScheduledCourseNotification[] | null;
}

export interface Course {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  title: string | null;
  company?: Company;
  /** @nullable */
  scenarios?: Scenario[] | null;
  /** @nullable */
  scheduledCourses?: ScheduledCourse[] | null;
}

export type ScenarioAccessLevel = (typeof ScenarioAccessLevel)[keyof typeof ScenarioAccessLevel];

export const ScenarioAccessLevel = {
  Read: 'Read'
} as const;

export interface ScenarioCompany {
  scenarioId?: number;
  companyId?: number;
  adminEditAccess?: ScenarioAccessLevel;
  /** @nullable */
  scenarioGroupId?: number | null;
  scenarioGroup?: ScenarioGroup;
}

export interface ScenarioGroup {
  id?: number;
  /** @nullable */
  title: string | null;
  company?: Company;
  companyId?: number;
  /** @nullable */
  scenarios?: Scenario[] | null;
  /** @nullable */
  sharedScenarios?: ScenarioCompany[] | null;
}

export interface ScenarioContextFact {
  id?: number;
  /** @nullable */
  text: string | null;
  sceneContextId?: number;
  scenarioContext?: ScenarioContext;
}

export interface ScenarioContext {
  id?: number;
  /** @nullable */
  facts?: ScenarioContextFact[] | null;
  scenarioId?: number;
  scenario?: Scenario;
}

export type Llm = (typeof Llm)[keyof typeof Llm];

export const Llm = {
  Gpt3_5: 'Gpt3_5',
  Gpt4: 'Gpt4',
  Gpt4o: 'Gpt4o',
  Claude3Haiku: 'Claude3Haiku',
  Claude3Sonnet: 'Claude3Sonnet',
  Claude3Opus: 'Claude3Opus',
  Claude35Sonnet: 'Claude35Sonnet',
  MistralTiny: 'MistralTiny',
  MistralSmall: 'MistralSmall',
  MistralMedium: 'MistralMedium',
  MistralLarge: 'MistralLarge',
  DBRX: 'DBRX',
  Llama3_8B: 'Llama3_8B',
  Llama3_70B: 'Llama3_70B',
  Gpt4oMini: 'Gpt4oMini',
  o3Mini: 'o3Mini',
  Gpt4_1: 'Gpt4_1',
  Gpt4_1mini: 'Gpt4_1mini',
  Gpt4_1nano: 'Gpt4_1nano',
  Gpt5_2: 'Gpt5_2',
  Gpt5_4: 'Gpt5_4',
  o4Mini: 'o4Mini',
  Gemini2_5Pro: 'Gemini2_5Pro',
  Gemini2_5Flash: 'Gemini2_5Flash',
  Gemini2_5FlashLite: 'Gemini2_5FlashLite',
  Gemini3Flash: 'Gemini3Flash',
  Gemini3_1FlashLite: 'Gemini3_1FlashLite',
  Gemini3_5Flash: 'Gemini3_5Flash',
  GptOss_120B: 'GptOss_120B',
  KimiK2Instruct_0905: 'KimiK2Instruct_0905',
  Qwen3_32B: 'Qwen3_32B',
  Gpt5_6Sol: 'Gpt5_6Sol',
  Gpt5_6Luna: 'Gpt5_6Luna',
  Gemini3_5FlashLite: 'Gemini3_5FlashLite',
  Gemini3_1Pro: 'Gemini3_1Pro',
  Gemini3_8Flash: 'Gemini3_8Flash',
  Gpt6Sol: 'Gpt6Sol',
  Gpt6Luna: 'Gpt6Luna'
} as const;

export interface VariableValue {
  id?: number;
  variableId?: number;
  variable?: Variable;
  /** @nullable */
  value?: string | null;
  /** @nullable */
  companyId?: number | null;
  company?: Company;
}

export interface Variable {
  id?: number;
  /** @nullable */
  name?: string | null;
  scenario?: Scenario;
  scenarioId?: number;
  /** @nullable */
  values?: VariableValue[] | null;
}

export type FileTypes = (typeof FileTypes)[keyof typeof FileTypes];

export const FileTypes = {
  ScenarioPdf: 'ScenarioPdf'
} as const;

export interface FileAttachment {
  id?: number;
  scenarioId?: number;
  scenario?: Scenario;
  fileType?: FileTypes;
  /** @nullable */
  blobName: string | null;
  /** @nullable */
  fileName: string | null;
  fileSize?: number;
  /** @nullable */
  checksumSha256: string | null;
  createdUtc?: string;
}

export interface Scenario {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  versionId?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  speed?: number;
  /** @nullable */
  langCode: string | null;
  /** @nullable */
  evaluationProtocolElements?: EvaluationProtocolElement[] | null;
  /** @nullable */
  blocks?: Block[] | null;
  /** @nullable */
  blockLinks?: BlockLink[] | null;
  /** @nullable */
  courses?: Course[] | null;
  sceneGroup?: ScenarioGroup;
  centerX?: number;
  centerY?: number;
  scale?: number;
  /** @nullable */
  scenarioContextIntro?: string | null;
  playScenarioContextOn?: boolean;
  /** @nullable */
  scenarioGroupId?: number | null;
  contextId?: number;
  context?: ScenarioContext;
  /**
   * @deprecated
   * @nullable
   */
  contexts?: ScenarioContext[] | null;
  isLlmEnabled?: boolean;
  llm?: Llm;
  /** @nullable */
  sharedCompanies?: Company[] | null;
  /** @nullable */
  scenarioCompanies?: ScenarioCompany[] | null;
  /** @nullable */
  scenarioPersonas?: ScenarioPersona[] | null;
  /** @nullable */
  variables?: Variable[] | null;
  /** @nullable */
  attachments?: FileAttachment[] | null;
  isPublished?: boolean;
  isDecisionSupportEnabled?: boolean;
  notesEnabled?: boolean;
  disableEndDetection?: boolean;
  paceCountingEnabled?: boolean;
  emotionalVoiceEnabled?: boolean;
  /** @nullable */
  backgroundSoundId?: number | null;
  backgroundSoundVolume?: number;
  /** @nullable */
  defaultCountingBpm?: number | null;
  /** @nullable */
  notesEvaluationProtocolElements?: EvaluationProtocolElement[] | null;
  maxTokens?: number;
}

export interface GlobalAssetCategory {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  language?: string | null;
  company?: Company;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  intents?: GlobalAssetIntent[] | null;
  /** @nullable */
  sharedCompanies?: Company[] | null;
}

export interface Tenant {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  domain: string | null;
  /** @nullable */
  tenantId: string | null;
  externalTenant?: boolean;
  /** @nullable */
  groups?: Group[] | null;
  /** @nullable */
  companies?: Company[] | null;
}

export interface Group {
  id?: number;
  /** @nullable */
  groupId: string | null;
  /** @nullable */
  name: string | null;
  tenant?: Tenant;
  tenantId?: number;
  company?: Company;
  companyId?: number;
}

export interface EvaluationProtocolCategory {
  id?: number;
  /** @nullable */
  name: string | null;
  order?: number;
  evaluationProtocolId?: number;
}

export interface EvaluationProtocol {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  languageCode: string | null;
  /** @nullable */
  sharedInstructions?: string | null;
  /** @nullable */
  evaluationStrategyJson?: string | null;
  /** @nullable */
  evaluationProtocolCompanies?: EvaluationProtocolCompany[] | null;
  /** @nullable */
  protocolCategories?: EvaluationProtocolCategory[] | null;
}

export interface EvaluationProtocolCompany {
  evaluationProtocolId?: number;
  evaluationProtocol?: EvaluationProtocol;
  companyId?: number;
  company?: Company;
  access?: AccessLevel;
}

export type Gender = (typeof Gender)[keyof typeof Gender];

export const Gender = {
  Female: 'Female',
  Male: 'Male'
} as const;

export type TtsProvider = (typeof TtsProvider)[keyof typeof TtsProvider];

export const TtsProvider = {
  Google: 'Google',
  InHouse: 'InHouse',
  Microsoft: 'Microsoft',
  ElevenLabs: 'ElevenLabs',
  Cartesia: 'Cartesia'
} as const;

export interface Persona {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  languageCode?: string | null;
  gender?: Gender;
  ttsProvider?: TtsProvider;
  /** @nullable */
  voiceName?: string | null;
  /** @nullable */
  voiceId?: string | null;
  /** @nullable */
  avatarUrl?: string | null;
  /** @nullable */
  personaCompanies?: PersonaCompany[] | null;
}

export interface PersonaCompany {
  personaId?: number;
  persona?: Persona;
  companyId?: number;
  company?: Company;
  access?: AccessLevel;
}

export interface Role {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  languageCode: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  scenarioPersonas?: ScenarioPersona[] | null;
  /** @nullable */
  roleCompanies?: RoleCompany[] | null;
}

export interface RoleCompany {
  roleId?: number;
  role?: Role;
  companyId?: number;
  company?: Company;
  access?: AccessLevel;
}

export interface Company {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  users?: User[] | null;
  /** @nullable */
  departments?: CompanyDepartment[] | null;
  /** @nullable */
  scenarios?: Scenario[] | null;
  /** @nullable */
  scenarioGroups?: ScenarioGroup[] | null;
  /** @nullable */
  sharedScenarios?: Scenario[] | null;
  /** @nullable */
  sharedGlobalAssetCategories?: GlobalAssetCategory[] | null;
  /** @nullable */
  globalAssetCategories?: GlobalAssetCategory[] | null;
  /** @nullable */
  readonly externalName?: string | null;
  /** @nullable */
  groups?: Group[] | null;
  /** @nullable */
  tenants?: Tenant[] | null;
  /** @nullable */
  defaultLanguage?: string | null;
  enableElevenLabsV3?: boolean;
  /** @nullable */
  licenseAgreements?: LicenseAgreement[] | null;
  /** @nullable */
  evaluationProtocolCompanies?: EvaluationProtocolCompany[] | null;
  /** @nullable */
  personaCompanies?: PersonaCompany[] | null;
  /** @nullable */
  roleCompanies?: RoleCompany[] | null;
}

export interface User {
  id?: number;
  role?: UserRole;
  /** @nullable */
  phone?: string | null;
  companyId?: number;
  company?: Company;
  /** @nullable */
  departmentId?: number | null;
  department?: CompanyDepartment;
  isAggreementAccepted?: boolean;
  hideScenarioContent?: boolean;
  speechThreshold?: number;
  speechWaitingTime?: number;
  smartTurnEnabled?: boolean;
  scenarioChatBargeInEnabled?: boolean;
  energyGateEnabled?: boolean;
  energyGateThresholdDb?: number;
  /** @nullable */
  dashboardReportId?: number | null;
  /** @nullable */
  userGroups?: UserGroup[] | null;
  /** @nullable */
  scheduledCourses?: ScheduledCourse[] | null;
  /** @nullable */
  scheduledCourseUsers?: ScheduledCourseUser[] | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  email: string | null;
  /** @nullable */
  externalUserId?: string | null;
  tenant?: Tenant;
  /** @nullable */
  tenantId?: number | null;
  group?: Group;
  /** @nullable */
  groupId?: number | null;
  hasSklls?: boolean;
  hasInterpreter?: boolean;
  hasExercises?: boolean;
  hasRecordings?: boolean;
}

export interface GlobalAssetSample {
  id?: number;
  intent?: GlobalAssetIntent;
  /** @nullable */
  text?: string | null;
}

export interface GlobalAssetIntent {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  /** @nullable */
  title?: string | null;
  category?: GlobalAssetCategory;
  categoryId?: number;
  /** @nullable */
  samples?: GlobalAssetSample[] | null;
  /** @nullable */
  blocks?: Block[] | null;
}

export interface AddBlockCommand {
  id?: number;
  /** @nullable */
  name: string | null;
  type?: BlockType;
  /** @nullable */
  description?: string | null;
  qualityLevel?: QualityLevel;
  /** @nullable */
  globalAssetIntents?: GlobalAssetIntent[] | null;
  /** @nullable */
  aiResponses?: AiVoiceResponse[] | null;
  /** @nullable */
  intentPhrases?: IntentPhrase[] | null;
  posX?: number;
  posY?: number;
  scenario?: Scenario;
  scenarioId?: number;
  scores?: number;
  /** @nullable */
  frontendBlockId?: string | null;
  /** @nullable */
  scenarioPersonaId?: number | null;
  scenarioPersona?: ScenarioPersona;
}

export interface AddBlockLinkCommand {
  id?: number;
  origin?: Block;
  originId?: number;
  target?: Block;
  targetId?: number;
  scenario?: Scenario;
  scenarioId?: number;
  /** @nullable */
  frontendOriginId?: string | null;
  /** @nullable */
  frontendTargetId?: string | null;
}

export type EndReason = (typeof EndReason)[keyof typeof EndReason];

export const EndReason = {
  Finish: 'Finish',
  CanceledByUser: 'CanceledByUser',
  BrowserClosed: 'BrowserClosed',
  Timeout: 'Timeout',
  Provisional: 'Provisional',
  Regenerate: 'Regenerate',
  CallerHungUp: 'CallerHungUp'
} as const;

export interface AddDialogStatisticCommand {
  sessionId?: string;
  endReason?: EndReason;
  isPhoneConversation?: boolean;
  /** @nullable */
  phoneNumber?: string | null;
}

export interface AddScenarioCommand {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  modifiedBy?: number | null;
  createdByUser?: User;
  modifiedByUser?: User;
  createdDate?: string;
  modifiedDate?: string;
  versionId?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  speed?: number;
  /** @nullable */
  langCode: string | null;
  /** @nullable */
  evaluationProtocolElements?: EvaluationProtocolElement[] | null;
  /** @nullable */
  blocks?: Block[] | null;
  /** @nullable */
  blockLinks?: BlockLink[] | null;
  /** @nullable */
  courses?: Course[] | null;
  sceneGroup?: ScenarioGroup;
  centerX?: number;
  centerY?: number;
  scale?: number;
  /** @nullable */
  scenarioContextIntro?: string | null;
  playScenarioContextOn?: boolean;
  /** @nullable */
  scenarioGroupId?: number | null;
  contextId?: number;
  context?: ScenarioContext;
  /**
   * @deprecated
   * @nullable
   */
  contexts?: ScenarioContext[] | null;
  isLlmEnabled?: boolean;
  llm?: Llm;
  /** @nullable */
  sharedCompanies?: Company[] | null;
  /** @nullable */
  scenarioCompanies?: ScenarioCompany[] | null;
  /** @nullable */
  scenarioPersonas?: ScenarioPersona[] | null;
  /** @nullable */
  variables?: Variable[] | null;
  /** @nullable */
  attachments?: FileAttachment[] | null;
  isPublished?: boolean;
  isDecisionSupportEnabled?: boolean;
  notesEnabled?: boolean;
  disableEndDetection?: boolean;
  paceCountingEnabled?: boolean;
  emotionalVoiceEnabled?: boolean;
  /** @nullable */
  backgroundSoundId?: number | null;
  backgroundSoundVolume?: number;
  /** @nullable */
  defaultCountingBpm?: number | null;
  /** @nullable */
  notesEvaluationProtocolElements?: EvaluationProtocolElement[] | null;
  maxTokens?: number;
}

export interface AddUsersToGroupCommand {
  /** @nullable */
  users?: number[] | null;
}

/**
 * Body for the backend-driven handover. Ends the current active role and activates the next one
 * over the same patient session. Optional ScenarioEditor.Api.Dto.Simulation.AdvanceStageDto.Summary is included on the handover event
 * (e.g. the field-to-ED handover summary).
 */
export interface AdvanceStageDto {
  /** @nullable */
  summary?: string | null;
}

export interface AiVoiceResponseDto {
  id?: number;
  /** @nullable */
  text: string | null;
  voiceResponseUsecase?: VoiceResponseUsecase;
}

export type ExerciseScheduleCascadeMode =
  (typeof ExerciseScheduleCascadeMode)[keyof typeof ExerciseScheduleCascadeMode];

export const ExerciseScheduleCascadeMode = {
  shift_all: 'shift_all',
  save_only: 'save_only'
} as const;

export interface ApplyExerciseScheduleChangeDto {
  /**
   * @minLength 0
   * @maxLength 200
   */
  title: string;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * Background given to every AI caller when shared memory is on. Null keeps the stored text; empty clears it.
   * @minLength 0
   * @maxLength 4000
   * @nullable
   */
  aiBriefing?: string | null;
  /**
   * Null keeps the stored value.
   * @nullable
   */
  sharedMemoryEnabled?: boolean | null;
  scheduledDateTime: string;
  scheduledEndDateTime: string;
  cascadeMode: ExerciseScheduleCascadeMode;
  originalScheduledDateTime: string;
  originalScheduledEndDateTime: string;
}

export interface ExerciseContactDto {
  id?: number;
  exerciseId?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  phoneNumber: string | null;
  /** @nullable */
  type: string | null;
  /**
   * Linked Sklls user for a Human contact; null for manual entry and AI contacts.
   * @nullable
   */
  userId?: number | null;
}

export interface ExerciseConversationDto {
  id?: number;
  playbookItemId?: number;
  dialogStatisticId?: number;
  /** @nullable */
  summary?: string | null;
}

export interface PlaybookItemDto {
  id?: number;
  exerciseId?: number;
  scenarioId?: number;
  /** @nullable */
  description?: string | null;
  /**
   * Personas outside the item's scenario that also know what happened in its calls.
   * @nullable
   */
  knownPersonaIds?: number[] | null;
  scheduledDateTime?: string;
  /** @nullable */
  endedDateTime?: string | null;
  /** @nullable */
  fromExerciseContactId?: number | null;
  toExerciseContactId?: number;
  /** @nullable */
  conversations?: ExerciseConversationDto[] | null;
  /** Whether a delayed job has been scheduled for this item. */
  isScheduled?: boolean;
  /**
   * When the call was actually executed. Null if not yet executed.
   * @nullable
   */
  executedDateTime?: string | null;
}

export interface ExerciseDto {
  id?: number;
  companyId?: number;
  /** @nullable */
  title: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  aiBriefing?: string | null;
  sharedMemoryEnabled?: boolean;
  /** A draft keeps its dates but never starts or places calls until a person schedules it. */
  isDraft?: boolean;
  scheduledDateTime?: string;
  scheduledEndDateTime?: string;
  /** @nullable */
  startedDateTime?: string | null;
  /** @nullable */
  endedDateTime?: string | null;
  /** @nullable */
  contactList?: ExerciseContactDto[] | null;
  /** @nullable */
  playbook?: PlaybookItemDto[] | null;
}

export interface ApplyExerciseScheduleChangeResponseDto {
  exercise: ExerciseDto;
  shiftedItemCount?: number;
  shiftMinutes?: number;
  cascadeMode?: ExerciseScheduleCascadeMode;
  endDateAutoAdjusted?: boolean;
}

export type PlaybookTimeCascadeMode = (typeof PlaybookTimeCascadeMode)[keyof typeof PlaybookTimeCascadeMode];

export const PlaybookTimeCascadeMode = {
  shift_following: 'shift_following',
  save_only: 'save_only'
} as const;

export interface ApplyPlaybookItemTimeChangeDto {
  scenarioId: number;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * Personas outside the item's scenario that also know what happened in its calls. Null keeps the stored list.
   * @nullable
   */
  knownPersonaIds?: number[] | null;
  scheduledDateTime: string;
  /** @nullable */
  fromExerciseContactId?: number | null;
  toExerciseContactId: number;
  cascadeMode: PlaybookTimeCascadeMode;
  originalItemScheduledDateTime: string;
  expectedExerciseScheduledDateTime: string;
  expectedExerciseScheduledEndDateTime: string;
}

export interface ApplyPlaybookItemTimeChangeResponseDto {
  item: PlaybookItemDto;
  shiftedItemCount?: number;
  shiftMinutes?: number;
  cascadeMode?: PlaybookTimeCascadeMode;
}

export interface AttachmentDto {
  /** @nullable */
  url: string | null;
  /** @nullable */
  fileName: string | null;
  fileSize?: number;
}

export interface RoleDto {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  languageCode: string | null;
  /** @nullable */
  description?: string | null;
  access?: AccessLevel;
  otherCompaniesCount?: number;
}

/**
 * DTO returned inside ScenarioEditor.Api.Dto.Persona.PersonaDto that shows
 * which company has which access level to the persona.
 */
export interface PersonaCompanyDto {
  id?: number;
  companyId?: number;
  access?: AccessLevel;
}

/**
 * Public-facing representation of a Persona.
 * Includes the per-company access level instead of plain Ids.
 */
export interface PersonaDto {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description: string | null;
  /** @nullable */
  languageCode: string | null;
  gender?: Gender;
  ttsProvider?: TtsProvider;
  /** @nullable */
  voiceName: string | null;
  /** @nullable */
  voiceId: string | null;
  /** @nullable */
  avatarUrl: string | null;
  /**
   * List of companies that have this persona assigned, together with
   * their access level (read / edit).
   * @nullable
   */
  companies?: PersonaCompanyDto[] | null;
  /** Number of <em>other</em> companies (excluding the caller's company) this persona is shared with. */
  otherCompaniesCount?: number;
}

export interface ScenarioPersonaDto {
  id?: number;
  scenarioId?: number;
  personaId?: number;
  /** @nullable */
  roleId?: number | null;
  /** @nullable */
  personaUsages?: PersonaUsage[] | null;
  role?: RoleDto;
  /** @nullable */
  roleName?: string | null;
  persona: PersonaDto;
}

export type UserCountingAssessment = (typeof UserCountingAssessment)[keyof typeof UserCountingAssessment];

export const UserCountingAssessment = {
  NotMeasured: 'NotMeasured',
  TooSlow: 'TooSlow',
  InRange: 'InRange',
  TooFast: 'TooFast'
} as const;

/**
 * The user's measured counting pace for one turn, against the recommended band.
 */
export interface UserCountingPaceDto {
  /** @nullable */
  bpm?: number | null;
  /** @nullable */
  confidence?: number | null;
  assessment?: UserCountingAssessment;
  targetMinBpm?: number;
  targetMaxBpm?: number;
}

export interface AudioResponseDto {
  /** @nullable */
  text: string | null;
  /** @nullable */
  audio: string | null;
  /** @nullable */
  lastBlockId?: number | null;
  isEnd?: boolean;
  persona: ScenarioPersonaDto;
  /** @nullable */
  avatarUrl?: string | null;
  userCountingPace?: UserCountingPaceDto;
}

export interface Avatar {
  /** @nullable */
  collection?: string | null;
  gender?: Gender;
  /** @nullable */
  url?: string | null;
}

export type BackgroundContextKind = (typeof BackgroundContextKind)[keyof typeof BackgroundContextKind];

export const BackgroundContextKind = {
  Handover: 'Handover',
  Exercise: 'Exercise'
} as const;

export interface BackgroundSoundDto {
  id?: number;
  /** @nullable */
  name: string | null;
  fileSizeBytes?: number;
  durationMs?: number;
  /** Measured loudness; players level every sound to the same target with it. */
  rmsDbfs?: number;
  createdUtc?: string;
}

export interface GlobalAssetSampleDto {
  id?: number;
  /** @nullable */
  text: string | null;
}

export interface GlobalAssetIntentDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title: string | null;
  categoryId?: number;
  /** @nullable */
  samples?: GlobalAssetSampleDto[] | null;
}

export interface IntentPhraseDto {
  id?: number;
  /** @nullable */
  text: string | null;
}

export interface BlockDto {
  id?: number;
  /** @nullable */
  name: string | null;
  type?: BlockType;
  /** @nullable */
  description?: string | null;
  qualityLevel?: QualityLevel;
  /** @nullable */
  globalAssetIntents?: GlobalAssetIntentDto[] | null;
  /** @nullable */
  aiResponses?: AiVoiceResponseDto[] | null;
  /** @nullable */
  intentPhrases?: IntentPhraseDto[] | null;
  posX?: number;
  posY?: number;
  sceneId?: number;
  /** @nullable */
  scenarioPersonaId?: number | null;
}

export interface BlockLinkDto {
  id?: number;
  origin: BlockDto;
  originId?: number;
  target: BlockDto;
  targetId?: number;
  sceneId?: number;
}

export interface BulkRescheduleStoreProvisionalDialogStatisticRequest {
  /** Lookback window in minutes from current UTC time. */
  lookbackMinutes?: number;
  /** If true, returns candidates only and does not enqueue new jobs. */
  dryRun?: boolean;
  /** If true, only includes sessions where the corresponding DialogStatistic is missing or not finalized. */
  onlyMissingOrUnendedStatistics?: boolean;
}

export interface CallerTurnRequest {
  /** @nullable */
  text?: string | null;
}

export interface CloneExerciseResponseDto {
  id: number;
  /** @nullable */
  title: string | null;
}

export interface CompanyDepartmentDto {
  id?: number;
  /** @nullable */
  name?: string | null;
  companyId?: number;
}

export interface EvaluationProtocolElementDto {
  id?: number;
  evaluationProtocolCategoryId?: number;
  order?: number;
  /** @nullable */
  name?: string | null;
  type?: EvaluationProtocolElementType;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  /** @nullable */
  choices?: string | null;
  explanationEnabled?: boolean;
  allowNotApplicable?: boolean;
  allowMultiple?: boolean;
  /** @nullable */
  goalSeconds?: number | null;
  excludedFromScore?: boolean;
  /** @nullable */
  description?: string | null;
}

export interface ScenarioContextFactDto {
  id?: number;
  /** @nullable */
  text: string | null;
}

export interface ScenarioContextDto {
  id?: number;
  /** @nullable */
  facts?: ScenarioContextFactDto[] | null;
}

export type EmotionalVoiceAvailability = (typeof EmotionalVoiceAvailability)[keyof typeof EmotionalVoiceAvailability];

export const EmotionalVoiceAvailability = {
  Available: 'Available',
  UnsupportedLanguage: 'UnsupportedLanguage',
  NoElevenLabsVoice: 'NoElevenLabsVoice'
} as const;

export interface VariableValueDto {
  id?: number;
  /** @nullable */
  value: string | null;
  /** @nullable */
  companyId?: number | null;
}

export interface VariableDto {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  value?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  values?: VariableValueDto[] | null;
}

export interface ScenarioDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  versionId?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  speed?: number;
  /** @nullable */
  langCode: string | null;
  ttsProvider?: TtsProvider;
  /** @nullable */
  evaluationProtocolElements?: EvaluationProtocolElementDto[] | null;
  /** @nullable */
  blocks?: BlockDto[] | null;
  /** @nullable */
  blockLinks?: BlockLinkDto[] | null;
  centerX?: number;
  centerY?: number;
  scale?: number;
  /** @nullable */
  scenarioContextIntro?: string | null;
  playScenarioContextOn?: boolean;
  startBlockId?: number;
  sceneGroupId?: number;
  /** @nullable */
  companyName?: string | null;
  context?: ScenarioContextDto;
  isLlmEnabled?: boolean;
  llm?: Llm;
  isDecisionSupportEnabled?: boolean;
  notesEnabled?: boolean;
  disableEndDetection?: boolean;
  paceCountingEnabled?: boolean;
  /** @nullable */
  defaultCountingBpm?: number | null;
  emotionalVoiceEnabled?: boolean;
  emotionalVoiceAvailability?: EmotionalVoiceAvailability;
  /** @nullable */
  backgroundSoundId?: number | null;
  backgroundSoundVolume?: number;
  /** @nullable */
  notesEvaluationProtocolElements?: EvaluationProtocolElementDto[] | null;
  maxTokens?: number;
  /** @nullable */
  sharedCompanies?: CompanyDto[] | null;
  /** @nullable */
  scenarioPersonas?: ScenarioPersonaDto[] | null;
  /** @nullable */
  variables?: VariableDto[] | null;
  isPublished?: boolean;
  hasPdf?: boolean;
}

export interface ScenarioGroupDto {
  id?: number;
  /** @nullable */
  title: string | null;
  companyId?: number;
  /** @nullable */
  sceneIds?: number[] | null;
  /** @nullable */
  scenes?: ScenarioDto[] | null;
}

export interface CompanyDto {
  id?: number;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  defaultLanguage?: string | null;
  enableElevenLabsV3?: boolean;
  /** @nullable */
  sceneGroups?: ScenarioGroupDto[] | null;
  /** @nullable */
  scenes?: ScenarioDto[] | null;
}

export type SortType = (typeof SortType)[keyof typeof SortType];

export const SortType = {
  Asc: 'Asc',
  Desc: 'Desc'
} as const;

export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

export const ExportFormat = {
  Xlsx: 'Xlsx',
  Csv: 'Csv'
} as const;

export interface ConversationExportFilterDto {
  /** @nullable */
  from?: string | null;
  /** @nullable */
  to?: string | null;
  /** @nullable */
  search?: string | null;
  /** @nullable */
  companyName?: string | null;
  /** @nullable */
  departmentName?: string | null;
  /** @nullable */
  scenarioName?: string | null;
  /** @nullable */
  userName?: string | null;
  /** @nullable */
  durationMin?: number | null;
  /** @nullable */
  durationMax?: number | null;
  /** @nullable */
  scoreMin?: number | null;
  /** @nullable */
  scoreMax?: number | null;
  /** @nullable */
  isPhoneConversation?: boolean | null;
  /** @nullable */
  timeZoneId?: string | null;
  /** @nullable */
  requestedByUserId?: number | null;
  sortOrder?: SortType;
  /** @nullable */
  orderBy?: string | null;
  format?: ExportFormat;
}

export type FeedbackEmoji = (typeof FeedbackEmoji)[keyof typeof FeedbackEmoji];

export const FeedbackEmoji = {
  Good: 'Good',
  Neutral: 'Neutral',
  Bad: 'Bad'
} as const;

export interface ConversationFeedback {
  emoji?: FeedbackEmoji;
  /** @nullable */
  feedback?: string | null;
  /** @nullable */
  metadata?: string | null;
}

export interface ConvertFromSharedScenarioCommand {
  targetCompanyId?: number;
}

export interface CopyScenarioCommand {
  id?: number;
  /** @nullable */
  newName?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  targetLanguage?: string | null;
  shareProtocols?: boolean;
  sharePersonas?: boolean;
}

export interface CountingPacing {
  /** @nullable */
  pacerText?: string | null;
  bpm?: number;
}

export interface CourseScenarioDto {
  id?: number;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  description?: string | null;
}

export interface ScheduledCourseUserDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  name?: string | null;
  userId?: number;
}

export interface ScheduledCourseUserGroupDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title?: string | null;
  userGroupId?: number;
}

export interface ScheduledCourseDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  courseId?: number;
  departmentId?: number;
  /** @nullable */
  departmentName?: string | null;
  /** @nullable */
  courseTitle?: string | null;
  completedScenesCount?: number;
  scenesCount?: number;
  startDate?: string;
  endDate?: string;
  /** @nullable */
  users?: ScheduledCourseUserDto[] | null;
  /** @nullable */
  userGroups?: ScheduledCourseUserGroupDto[] | null;
}

export interface CourseDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title?: string | null;
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
  scenesCount?: number;
  /** @nullable */
  scenes?: CourseScenarioDto[] | null;
  /** @nullable */
  scheduledCourses?: ScheduledCourseDto[] | null;
}

export interface CreateCompanyCommand {
  /** @nullable */
  name?: string | null;
  enableElevenLabsV3?: boolean;
}

export interface CreateCompanyDepartmentCommand {
  /** @nullable */
  name?: string | null;
}

export interface CreateCourseCommand {
  companyId?: number;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  duration?: number | null;
}

export interface CreateEvaluationProtocolCategoryCommand {
  evaluationProtocolId?: number;
  /** @nullable */
  name?: string | null;
}

export interface ProtocolCompanyAccessDto {
  companyId?: number;
  access?: AccessLevel;
}

export interface CreateEvaluationProtocolElementCommand {
  evaluationProtocolId?: number;
  evaluationProtocolCategoryId?: number;
  /** @nullable */
  name?: string | null;
  type?: EvaluationProtocolElementType;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  /** @nullable */
  choices?: string | null;
  explanationEnabled?: boolean;
  /** @nullable */
  description?: string | null;
  allowNotApplicable?: boolean;
  allowMultiple?: boolean;
  /** @nullable */
  goalSeconds?: number | null;
  excludedFromScore?: boolean;
}

export interface CreateEvaluationProtocolCommand {
  /** @nullable */
  companies?: ProtocolCompanyAccessDto[] | null;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  languageCode?: string | null;
  /** @nullable */
  items?: CreateEvaluationProtocolElementCommand[] | null;
  /** @nullable */
  sharedInstructions?: string | null;
}

export interface CreateExerciseContactDto {
  /**
   * @minLength 0
   * @maxLength 200
   */
  name: string;
  /**
   * @minLength 0
   * @maxLength 50
   */
  phoneNumber: string;
  /** @minLength 1 */
  type: string;
  /**
   * Linked Sklls user for a Human contact; null for manual entry and AI contacts.
   * @nullable
   */
  userId?: number | null;
}

export interface CreatePlaybookItemDto {
  scenarioId: number;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * Personas outside the item's scenario that also know what happened in its calls.
   * @nullable
   */
  knownPersonaIds?: number[] | null;
  scheduledDateTime: string;
  /** @nullable */
  fromExerciseContactId?: number | null;
  toExerciseContactId: number;
}

export interface CreateExerciseDto {
  /**
   * @minLength 0
   * @maxLength 200
   */
  title: string;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * @minLength 0
   * @maxLength 4000
   * @nullable
   */
  aiBriefing?: string | null;
  /**
   * Briefing and memory of earlier calls reach the AI. Off unless the instructor turns it on (null counts as off).
   * @nullable
   */
  sharedMemoryEnabled?: boolean | null;
  scheduledDateTime: string;
  scheduledEndDateTime: string;
  /**
   * Organization to create the exercise in. Only used for SuperAdmins. When omitted, the
   * exercise goes to the caller's own organization.
   * @nullable
   */
  companyId?: number | null;
  /** @nullable */
  contactList?: CreateExerciseContactDto[] | null;
  /** @nullable */
  playbook?: CreatePlaybookItemDto[] | null;
}

export interface CreateGlobalAssetCategoryCommand {
  /** @nullable */
  title?: string | null;
  /** @nullable */
  language?: string | null;
  /** @nullable */
  shared?: boolean | null;
}

export interface CreateGlobalAssetIntentCommand {
  /** @nullable */
  title?: string | null;
  /** @nullable */
  samples?: string[] | null;
}

export interface CreateLicenseAgreementDto {
  companyId: number;
  /** @nullable */
  departmentId?: number | null;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  skllsSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  interpreterSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  exercisesSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  recordingsSeats: number;
  startTime: string;
  expiryTime: string;
}

export interface CreateOrganizationInvitationCommand {
  companyId?: number;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  tenantId?: string | null;
  /** @nullable */
  tenantDomain?: string | null;
  hasSklls?: boolean;
  hasInterpreter?: boolean;
  hasExercises?: boolean;
  hasRecordings?: boolean;
  role?: UserRole;
}

/**
 * DTO for creating a new persona.
 */
export interface CreatePersonaDto {
  /**
   * The name of the persona.
   * @minLength 0
   * @maxLength 100
   */
  name: string;
  /**
   * Optional description of the persona. Behavioral personas (e.g. agonal breathing patients)
   * carry their full behavioral contract here and need far more than the template's 500 characters.
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * The language code for the persona (e.g., "en-US", "sv-SE").
   * @minLength 0
   * @maxLength 10
   */
  languageCode: string;
  gender: Gender;
  ttsProvider: TtsProvider;
  /**
   * The voice name to use with the TTS provider. Deprecated; prefer voiceId.
   * @minLength 0
   * @maxLength 100
   */
  voiceName: string;
  /**
   * Provider-stable voice identifier (e.g., Azure ShortName, Google voice name, ElevenLabs voice_id).
   * @minLength 0
   * @maxLength 100
   * @nullable
   */
  voiceId?: string | null;
  /**
   * Optional URL to an avatar image for the persona.
   * @minLength 0
   * @maxLength 500
   * @nullable
   */
  avatarUrl?: string | null;
}

export interface CreateRecordingEvaluationRunRequest {
  /**
   * Criteria (evaluation protocol element ids) to evaluate; may span several protocols.
   * @nullable
   */
  evaluationProtocolElementIds?: number[] | null;
}

export interface CreateRoleDto {
  /** @nullable */
  name: string | null;
  /** @nullable */
  languageCode: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  companyId?: number | null;
}

export interface CreateScheduledCallCommand {
  scenarioId?: number;
  scheduledTime?: string;
  /** @nullable */
  targetName?: string | null;
  /** @nullable */
  userId?: number | null;
  /** @nullable */
  phoneNumber?: string | null;
  companyId?: number;
}

export interface CreateScheduledCourseCommand {
  /** @nullable */
  departmentId?: number | null;
  startDate?: string;
  endDate?: string;
  addAllUsers?: boolean;
}

export interface CreateScheduledCourseNotificationCommand {
  date?: string;
  /** @nullable */
  customText?: string | null;
}

export interface CreateSimulationStageDto {
  sortOrder?: number;
  /**
   * @minLength 0
   * @maxLength 200
   */
  name: string;
  scenarioSideAId: number;
  /**
   * SideB scenario, or null when SideB is driven by the agent engine (the operator side).
   * @nullable
   */
  scenarioSideBId?: number | null;
}

export interface CreateSimulationDto {
  /**
   * @minLength 0
   * @maxLength 200
   */
  title: string;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /** @nullable */
  stages?: CreateSimulationStageDto[] | null;
}

export interface CreateUserGroupCommand {
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  title?: string | null;
}

/**
 * Creates a VITAL simulation from a single selected patient scenario. The backend
 * generates the fixed 3-stage journey (operator → paramedic → doctor) around it using
 * ScenarioEditor.Core.Common.Options.VitalSimulationOptions.
 */
export interface CreateVitalSimulationDto {
  /** The selected patient scenario, present as SideA in all three stages. */
  patientScenarioId: number;
  /**
   * @minLength 0
   * @maxLength 200
   * @nullable
   */
  title?: string | null;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
}

export interface DecisionSupportRequest {
  sessionId: string;
  /**
   * Message from the caller (patient/innringer) that requires operator advice
   * @nullable
   */
  callerMessage?: string | null;
  /**
   * The operator's actual response to the previous third party message (for conversation tracking)
   * @nullable
   */
  operatorResponse?: string | null;
}

export interface DecisionSupportResponse {
  sessionId?: string;
  /** @nullable */
  message?: string | null;
  /** @nullable */
  completedTasks?: string[] | null;
  isEnd?: boolean;
}

/**
 * Structured response for decision support providing tactical guidance to emergency operators.
 * Contains the four key components of operator guidance in a structured format for frontend consumption.
 */
export interface DecisionSupportStructuredResponseDto {
  sessionId?: string;
  /**
   * Brief recommendation for the operator's next action
   * @nullable
   */
  suggest?: string | null;
  /**
   * Exact wording the operator can use with the caller
   * @nullable
   */
  say?: string | null;
  /**
   * Brief justification based on observations or protocol; flags uncertainty if lacking data
   * @nullable
   */
  rationale?: string | null;
  /**
   * Control questions or observations the operator should make now; 'Ingen' if not applicable
   * @nullable
   */
  checks?: string | null;
  /**
   * List of completed tasks in this interaction
   * @nullable
   */
  completedTasks?: string[] | null;
  /** Indicates if this is the end of the conversation */
  isEnd?: boolean;
}

export type OnCompanyDeleteAction = (typeof OnCompanyDeleteAction)[keyof typeof OnCompanyDeleteAction];

export const OnCompanyDeleteAction = {
  DeleteUsers: 'DeleteUsers',
  MoveUsers: 'MoveUsers'
} as const;

export interface DeleteCompanyCommand {
  id?: number;
  onCompanyDeleteAction?: OnCompanyDeleteAction;
  /** @nullable */
  destinationCompanyId?: number | null;
}

export interface DeleteGlobalAssetCategoriesCommand {
  /** @nullable */
  ids?: number[] | null;
}

export interface DeleteGlobalAssetIntentCommand {
  /** @nullable */
  ids?: number[] | null;
}

export interface DeleteScheduledCallsCommand {
  /** @nullable */
  ids?: number[] | null;
}

export interface DeleteUserGroupCommand {
  /** @nullable */
  ids?: number[] | null;
}

export interface DeleteUsersFromGroupCommand {
  /** @nullable */
  ids?: number[] | null;
}

export type InvitationStatus = (typeof InvitationStatus)[keyof typeof InvitationStatus];

export const InvitationStatus = {
  Invited: 'Invited',
  Expired: 'Expired',
  Accepted: 'Accepted',
  Revoked: 'Revoked'
} as const;

export interface DepartmentInvitationDto {
  id?: number;
  /** @nullable */
  inviteeEmail?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  inviterId?: number | null;
  /** @nullable */
  createdTime?: string | null;
  /** @nullable */
  expirationTime?: string | null;
  /** @nullable */
  acceptedTime?: string | null;
  role?: UserRole;
  invitationStatus?: InvitationStatus;
}

export interface DialogFeedbackDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  emoji?: FeedbackEmoji;
  /** @nullable */
  feedback: string | null;
  /** @nullable */
  metadata: string | null;
}

export interface DialogStatisticRespondentDto {
  userId?: number;
  /** @nullable */
  userName: string | null;
  /** @nullable */
  email: string | null;
}

export interface DialogStatisticIntentCountingDto {
  /**
   * The tempo written on the count tag; null when the persona counted without one.
   * @nullable
   */
  bpm?: number | null;
}

export interface DialogStatisticIntentPredictionSampleDto {
  /** @nullable */
  text: string | null;
}

export interface DialogStatisticGlobalIntentPredictionSampleDto {
  /** @nullable */
  text: string | null;
}

export interface DialogStatisticGlobalIntentPredictionDto {
  globalIntentId?: number;
  /** @nullable */
  globalIntentName: string | null;
  /** @nullable */
  globalIntentSamples?: DialogStatisticGlobalIntentPredictionSampleDto[] | null;
  /** @nullable */
  globalCategoryName?: string | null;
  originalIntentId?: number;
}

export interface DialogStatisticIntentPredictionDto {
  originalIntentId?: number;
  /** @nullable */
  originalIntentName: string | null;
  /** @nullable */
  samples?: DialogStatisticIntentPredictionSampleDto[] | null;
  /** @nullable */
  globalIntentsInPrediction?: DialogStatisticGlobalIntentPredictionDto[] | null;
}

export interface DialogStatisticIntentDto {
  id?: number;
  /** @nullable */
  voiceId?: string | null;
  /**
   * Saved audio of the AI reply as it was played (fetch it like VoiceId). The clip can be
   *             missing, e.g. when playback was cut off early; replay then synthesizes the reply. Read-only.
   * @nullable
   */
  aiVoiceId?: string | null;
  /**
   * How much of the AI reply the learner heard before interrupting it, in ms; replay stops the
   *             saved clip there. Null when the reply was not interrupted. Read-only.
   * @nullable
   */
  aiHeardDurationMs?: number | null;
  number?: number;
  currentBlock?: BlockDto;
  nextBlock?: BlockDto;
  start?: string;
  end?: string;
  /** @nullable */
  userSays: string | null;
  /**
   * The persona's line as shown: no counting markup, no audio tags.
   * @nullable
   */
  responseText: string | null;
  /**
   * The persona's line exactly as synthesised, for replaying the audio. Read-only.
   * @nullable
   */
  spokenText?: string | null;
  counting?: DialogStatisticIntentCountingDto;
  /** @nullable */
  originalIntentText?: string | null;
  /** @nullable */
  originalIntentId?: number | null;
  /** @nullable */
  originalIntentName?: string | null;
  isFallback?: boolean;
  /** @nullable */
  selectedScenarioPersonaId?: number | null;
  /**
   * Display info for the AI speaker of ResponseText (the persona behind SelectedScenarioPersonaId,
   *             or the scenario's default persona). Null when the scenario version could not be resolved.
   * @nullable
   */
  aiSpeakerName?: string | null;
  /** @nullable */
  aiSpeakerAvatarUrl?: string | null;
  verifyStatus?: boolean;
  scores?: number;
  corrected?: boolean;
  color?: QualityLevel;
  previousColor?: QualityLevel;
  /** @nullable */
  intentsInPrediction?: DialogStatisticIntentPredictionDto[] | null;
  /**
   * Measured counting pace of the user's turn; null when no user counting was measured.
   * @nullable
   */
  userCountingBpm?: number | null;
}

export interface QualitySettingsDto {
  origin?: QualityLevel;
  destination?: QualityLevel;
  value?: number;
}

export interface DialogStatisticDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  companyId?: number;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  departmentName: string | null;
  sceneId?: number;
  sceneVersionId?: number;
  /** @nullable */
  langCode: string | null;
  sessionId?: string;
  /** @nullable */
  sceneName: string | null;
  start?: string;
  /** @nullable */
  end?: string | null;
  respondent: DialogStatisticRespondentDto;
  timestamp?: string;
  /** @nullable */
  intents?: DialogStatisticIntentDto[] | null;
  /** @nullable */
  qualityParams?: QualitySettingsDto[] | null;
  /** @nullable */
  departmentId?: number | null;
  totalScores?: number;
  totalScoresMax?: number;
  /** @nullable */
  callRecordingUrl: string | null;
  /** @nullable */
  totalEvaluationScore?: number | null;
  /** @nullable */
  notesEvaluationScore?: number | null;
  notesEvaluationScorePending?: boolean;
  notesEvaluationFailed?: boolean;
  /** @nullable */
  summaryText?: string | null;
  summaryPending?: boolean;
  summaryFailed?: boolean;
  isPhoneConversation?: boolean;
  /** @nullable */
  phoneNumber?: string | null;
  hasNotes?: boolean;
}

export type EvaluationType = (typeof EvaluationType)[keyof typeof EvaluationType];

export const EvaluationType = {
  Conversation: 'Conversation',
  Notes: 'Notes',
  Recording: 'Recording'
} as const;

export type YesNoAnswer = (typeof YesNoAnswer)[keyof typeof YesNoAnswer];

export const YesNoAnswer = {
  Yes: 'Yes',
  No: 'No'
} as const;

/**
 * The immutable element-definition snapshot an evaluation was generated against. Replaces the
 * former inline JSON string. Category/protocol fields reflect the current (live) values, since
 * only elements are versioned.
 */
export interface EvaluationProtocolElementVersionDto {
  id?: number;
  /** @nullable */
  sourceElementId?: number | null;
  versionNumber?: number;
  order?: number;
  /** @nullable */
  name?: string | null;
  type?: EvaluationProtocolElementType;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  /** @nullable */
  choices?: string | null;
  explanationEnabled?: boolean;
  allowNotApplicable?: boolean;
  allowMultiple?: boolean;
  /** @nullable */
  goalSeconds?: number | null;
  excludedFromScore?: boolean;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  categoryName?: string | null;
  /** @nullable */
  categoryOrder?: number | null;
  /** @nullable */
  protocolName?: string | null;
  /** @nullable */
  protocolLanguageCode?: string | null;
}

export interface EvaluationElementDto {
  id?: number;
  evaluationId?: number;
  /** @nullable */
  score?: number | null;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  choice?: string | null;
  /** @nullable */
  yesNo?: boolean | null;
  yesNoAnswer?: YesNoAnswer;
  /** @nullable */
  startSeconds?: number | null;
  /** @nullable */
  endSeconds?: number | null;
  /**
   * Deliberately marked not applicable (Yes/No or numeric); excluded from scoring. For multiple
   * choice it means "none of these": no choice described the conversation. For elapsed time, the
   * criterion does not apply or its start never happened.
   */
  notApplicable?: boolean;
  /** @nullable */
  explanation?: string | null;
  evaluationProtocolElement?: EvaluationProtocolElementVersionDto;
}

export interface EvaluationDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  companyId?: number;
  /** @nullable */
  evaluationProtocolId?: number | null;
  /** @nullable */
  protocolSharedInstructionsSnapshot?: string | null;
  type?: EvaluationType;
  /** @nullable */
  evaluationElements?: EvaluationElementDto[] | null;
}

export interface DialogStatisticKpiDetailsDto {
  aiAnalysisCompleted?: boolean;
  evaluationScorePending?: boolean;
  notesEvaluationScorePending?: boolean;
  hasNotes?: boolean;
  /** @nullable */
  summaryText?: string | null;
  summaryPending?: boolean;
  summaryFailed?: boolean;
  /** @nullable */
  evaluations?: EvaluationDto[] | null;
  /** @nullable */
  notesEvaluations?: EvaluationDto[] | null;
}

/**
 * Body for marking an ambulance dispatched (transport mode begins). Carries optional unit and ETA
 * for the AmbulanceDispatched event; does not change the active role (the ambulance is still
 * observing until it advances on "arrived at patient").
 */
export interface DispatchAmbulanceDto {
  /** @nullable */
  unit?: string | null;
  /** @nullable */
  etaSeconds?: number | null;
  /**
   * Optional free-text description of the situation the ambulance drives into; seeds the
   * world-sim drive narrative (what can happen en route).
   * @nullable
   */
  situationDescription?: string | null;
}

export interface EditUserAudioSettingsCommand {
  speechThreshold?: number;
  speechWaitingTime?: number;
  smartTurnEnabled?: boolean;
  /** @nullable */
  energyGateEnabled?: boolean | null;
  /** @nullable */
  energyGateThresholdDb?: number | null;
}

export interface EditUserCommand {
  id?: number;
  role?: UserRole;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  name?: string | null;
  hideScenarioContent?: boolean;
  hasSklls?: boolean;
  hasInterpreter?: boolean;
  hasExercises?: boolean;
  hasRecordings?: boolean;
}

export interface EditUserProfileCommand {
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  scenarioChatBargeInEnabled?: boolean | null;
}

export type ScheduledCallStatus = (typeof ScheduledCallStatus)[keyof typeof ScheduledCallStatus];

export const ScheduledCallStatus = {
  Initial: 'Initial',
  InProgress: 'InProgress',
  Finished: 'Finished',
  Canceled: 'Canceled',
  Queued: 'Queued',
  NotCompleted: 'NotCompleted',
  NoAnswer: 'NoAnswer',
  Busy: 'Busy',
  WrongNumber: 'WrongNumber',
  Failed: 'Failed'
} as const;

export interface EndCallCommand {
  /** @nullable */
  callSid?: string | null;
  status?: ScheduledCallStatus;
}

export interface EvaluationProtocolCategoryDto {
  id?: number;
  /** @nullable */
  name?: string | null;
  order?: number;
  evaluationProtocolId?: number;
  /** @nullable */
  protocolElements?: EvaluationProtocolElementDto[] | null;
}

/**
 * DTO returned inside ScenarioEditor.Api.Dto.ConversationEvaluation.EvaluationProtocolDto that shows
 * which company has which access level to the protocol.
 */
export interface EvaluationProtocolCompanyDto {
  id?: number;
  companyId?: number;
  access?: AccessLevel;
}

/**
 * Public-facing representation of an Evaluation Protocol.
 * Now includes the per-company access level instead of plain Ids.
 */
export interface EvaluationProtocolDto {
  id?: number;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  languageCode?: string | null;
  /** @nullable */
  sharedInstructions?: string | null;
  /**
   * List of companies that have this protocol assigned, together with
   * their access level (read / edit).
   * @nullable
   */
  companies?: EvaluationProtocolCompanyDto[] | null;
  /** Number of <em>other</em> companies (excluding the caller’s company) this protocol is shared with. */
  otherCompaniesCount?: number;
  /** @nullable */
  protocolCategories?: EvaluationProtocolCategoryDto[] | null;
}

export interface EvaluationProtocolUsageDto {
  scenarioId?: number;
  /** @nullable */
  scenarioName: string | null;
  /** @nullable */
  organizationId?: number | null;
  /** @nullable */
  organizationName: string | null;
}

export type EvaluationStrategyConfigStatus =
  (typeof EvaluationStrategyConfigStatus)[keyof typeof EvaluationStrategyConfigStatus];

export const EvaluationStrategyConfigStatus = {
  legacy: 'legacy',
  modelLed: 'modelLed',
  disabledModelLed: 'disabledModelLed',
  unsupported: 'unsupported',
  invalid: 'invalid'
} as const;

export interface GenerateMagicPersonaPromptCommand {
  /** @nullable */
  text?: string | null;
  /** @nullable */
  instructions?: string | null;
  gender?: Gender;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  language?: string | null;
}

export interface GenerateMagicRolePromptCommand {
  /** @nullable */
  text?: string | null;
  /** @nullable */
  instructions?: string | null;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  language?: string | null;
}

export interface GeneratePersonaSentenceRequestDto {
  /** @minLength 1 */
  personaDescription: string;
  /** @nullable */
  topic?: string | null;
  /** @nullable */
  language?: string | null;
}

export interface GeneratePersonaSentenceResponseDto {
  /** @nullable */
  sentence?: string | null;
}

export interface GetLicenseUsageBatchRequest {
  companyIds: number[];
}

export interface GlobalAssetCategoryDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title: string | null;
  /** @nullable */
  language: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  sharedCompanies?: CompanyDto[] | null;
}

export interface GlobalAssetConnection {
  /** @nullable */
  name?: string | null;
  sceneId?: number;
}

export interface GlobalAssetFullDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title: string | null;
  /** @nullable */
  language: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  intents?: GlobalAssetIntentDto[] | null;
  /** @nullable */
  sharedCompanies?: CompanyDto[] | null;
}

export type HealthRule = (typeof HealthRule)[keyof typeof HealthRule];

export const HealthRule = {
  LlmDisabled: 'LlmDisabled',
  VoiceProvider: 'VoiceProvider',
  DuplicateRole: 'DuplicateRole',
  Model: 'Model',
  UnusedPersona: 'UnusedPersona',
  VoiceLang: 'VoiceLang',
  TooMany: 'TooMany',
  UnusedVar: 'UnusedVar'
} as const;

export type HealthSeverity = (typeof HealthSeverity)[keyof typeof HealthSeverity];

export const HealthSeverity = {
  High: 'High',
  Medium: 'Medium',
  Low: 'Low'
} as const;

export interface HuginAssistantChatCompanyOptionDto {
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
}

export interface HuginAssistantChatFileDto {
  /** @nullable */
  fileId?: string | null;
  /** @nullable */
  fileName?: string | null;
  /** @nullable */
  contentType?: string | null;
  /** @nullable */
  sizeBytes?: number | null;
  /** @nullable */
  uploadedAt?: string | null;
  /** @nullable */
  extractionStatus?: string | null;
  /** @nullable */
  warnings?: string[] | null;
}

export interface HuginAssistantChatUserOptionDto {
  userId?: number;
  /** @nullable */
  userName?: string | null;
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
}

export interface HuginAssistantChatFilterOptionsDto {
  /** @nullable */
  companies?: HuginAssistantChatCompanyOptionDto[] | null;
  /** @nullable */
  users?: HuginAssistantChatUserOptionDto[] | null;
}

export interface HuginAssistantChatSessionListItemDto {
  sessionId?: string;
  userId?: number;
  /** @nullable */
  userName?: string | null;
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
  createdAt?: string;
  lastActiveAt?: string;
  /** @nullable */
  name?: string | null;
  nameIsUserSet?: boolean;
}

export interface HuginAssistantChatToolDto {
  /** @nullable */
  toolCallId?: string | null;
  /** @nullable */
  toolName?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  isError?: boolean | null;
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  completedAt?: string | null;
  /** @nullable */
  statusKey?: string | null;
  statusParams?: unknown;
  /** @nullable */
  resultText?: string | null;
}

/**
 * @nullable
 */
export type HuginAssistantChatHistoryEventDtoMetadata = { [key: string]: unknown } | null;

export interface HuginAssistantChatHistoryEventDto {
  lineNumber?: number;
  /** @nullable */
  type?: string | null;
  /** @nullable */
  createdAt?: string | null;
  /** @nullable */
  role?: string | null;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  turnId?: string | null;
  tool?: HuginAssistantChatToolDto;
  file?: HuginAssistantChatFileDto;
  /** @nullable */
  display?: unknown[] | null;
  /** @nullable */
  metadata?: HuginAssistantChatHistoryEventDtoMetadata;
}

export interface HuginAssistantChatHistoryParseIssueDto {
  lineNumber?: number;
  /** @nullable */
  error?: string | null;
}

export interface HuginAssistantChatHistoryDto {
  session?: HuginAssistantChatSessionListItemDto;
  /** @nullable */
  events?: HuginAssistantChatHistoryEventDto[] | null;
  /** @nullable */
  parseIssues?: HuginAssistantChatHistoryParseIssueDto[] | null;
}

export interface InitPersonaSessionCommand {
  gender?: Gender;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  language?: string | null;
  ttsProvider?: TtsProvider;
  /** @nullable */
  voiceId?: string | null;
  /** @nullable */
  description?: string | null;
}

export interface InviteMultipleUsersCommand {
  companyId?: number;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  emails?: string[] | null;
  /** @nullable */
  tenantId?: string | null;
  /** @nullable */
  tenantDomain?: string | null;
  hasSklls?: boolean;
  hasInterpreter?: boolean;
  hasExercises?: boolean;
  hasRecordings?: boolean;
  role?: UserRole;
}

export interface IssueCandidateElement {
  elementId?: number;
  /** @nullable */
  rationale?: string | null;
}

export type IssueDedupMode = (typeof IssueDedupMode)[keyof typeof IssueDedupMode];

export const IssueDedupMode = {
  strict: 'strict',
  lenient: 'lenient'
} as const;

export interface IssueEvidence {
  /** @nullable */
  quote?: string | null;
  /** @nullable */
  location?: string | null;
}

export interface IssueLink {
  /** @nullable */
  relatedIssueId?: string | null;
  /** @nullable */
  relation?: string | null;
}

export type IssueSeverity = (typeof IssueSeverity)[keyof typeof IssueSeverity];

export const IssueSeverity = {
  Low: 'Low',
  Moderate: 'Moderate',
  High: 'High',
  Critical: 'Critical'
} as const;

export interface IssueTaxonomyEntry {
  /** @nullable */
  type?: string | null;
  /** @nullable */
  description?: string | null;
  defaultSeverity?: IssueSeverity;
}

/**
 * Data transfer object for job status
 */
export interface JobStatusDto {
  id?: number;
  /** The unique identifier of the job */
  jobId?: string;
  /**
   * The type of task being executed
   * @nullable
   */
  taskType: string | null;
  /**
   * Current status of the job (Queued, Processing, Completed, Failed)
   * @nullable
   */
  status: string | null;
  /** Time when the job was created */
  createdAt?: string;
  /**
   * Time when job processing started
   * @nullable
   */
  startedAt?: string | null;
  /**
   * Time when job processing was completed
   * @nullable
   */
  completedAt?: string | null;
  /** Time of the last status update */
  updatedAt?: string;
  /**
   * Error message if job failed
   * @nullable
   */
  errorMessage?: string | null;
  /**
   * Result summary when job completed successfully
   * @nullable
   */
  resultMessage?: string | null;
  /**
   * Payload in JSON format
   * @nullable
   */
  payloadJson: string | null;
  /**
   * The topic (LavinMQ stream name) the job was published to.
   * Used to distinguish jobs from different environments that share a database
   * (e.g. local dev vs. Azure dev).
   * @nullable
   */
  topic?: string | null;
}

export interface LicenseAgreementDto {
  id?: number;
  companyId?: number;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  departmentName?: string | null;
  skllsSeats?: number;
  interpreterSeats?: number;
  exercisesSeats?: number;
  recordingsSeats?: number;
  startTime?: string;
  expiryTime?: string;
}

export interface LicenseUsageDto {
  companyId?: number;
  /** @nullable */
  departmentId?: number | null;
  totalSkllsSeats?: number;
  usedSkllsSeats?: number;
  availableSkllsSeats?: number;
  totalInterpreterSeats?: number;
  usedInterpreterSeats?: number;
  availableInterpreterSeats?: number;
  totalExercisesSeats?: number;
  usedExercisesSeats?: number;
  availableExercisesSeats?: number;
  totalRecordingsSeats?: number;
  usedRecordingsSeats?: number;
  availableRecordingsSeats?: number;
  /** @nullable */
  sharedPoolSkllsSeats?: number | null;
  /** @nullable */
  sharedPoolSkllsUsed?: number | null;
  /** @nullable */
  sharedPoolSkllsAvailable?: number | null;
  /** @nullable */
  sharedPoolInterpreterSeats?: number | null;
  /** @nullable */
  sharedPoolInterpreterUsed?: number | null;
  /** @nullable */
  sharedPoolInterpreterAvailable?: number | null;
  /** @nullable */
  sharedPoolExercisesSeats?: number | null;
  /** @nullable */
  sharedPoolExercisesUsed?: number | null;
  /** @nullable */
  sharedPoolExercisesAvailable?: number | null;
  /** @nullable */
  sharedPoolRecordingsSeats?: number | null;
  /** @nullable */
  sharedPoolRecordingsUsed?: number | null;
  /** @nullable */
  sharedPoolRecordingsAvailable?: number | null;
}

export interface LlmUsageSummaryDto {
  totalScopes?: number;
  totalEntries?: number;
  totalTokens?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalCostUsd?: number;
  conversationScopes?: number;
  costPerConversationUsd?: number;
  /** @nullable */
  averageDurationMs?: number | null;
  /** @nullable */
  maxDurationMs?: number | null;
}

export type LlmUsageType = (typeof LlmUsageType)[keyof typeof LlmUsageType];

export const LlmUsageType = {
  AgentSession: 'AgentSession',
  RecordingTranscription: 'RecordingTranscription',
  Evaluation: 'Evaluation',
  Conversation: 'Conversation',
  ConversationEndDetection: 'ConversationEndDetection',
  DialogSummary: 'DialogSummary',
  ExerciseMemorySummary: 'ExerciseMemorySummary'
} as const;

export interface LlmUsageTypeAggregateDto {
  usageType?: LlmUsageType;
  scopes?: number;
  entries?: number;
  totalTokens?: number;
  totalCostUsd?: number;
  /** @nullable */
  averageDurationMs?: number | null;
  /** @nullable */
  maxDurationMs?: number | null;
}

export interface LlmUsageCompanyAggregateDto {
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
  scopes?: number;
  entries?: number;
  totalTokens?: number;
  totalCostUsd?: number;
  /** @nullable */
  averageDurationMs?: number | null;
  /** @nullable */
  maxDurationMs?: number | null;
}

export interface LlmUsageUserAggregateDto {
  userId?: number;
  /** @nullable */
  userName?: string | null;
  /** @nullable */
  companyName?: string | null;
  scopes?: number;
  entries?: number;
  totalTokens?: number;
  totalCostUsd?: number;
  /** @nullable */
  averageDurationMs?: number | null;
  /** @nullable */
  maxDurationMs?: number | null;
}

export interface LlmUsageModelAggregateDto {
  /** @nullable */
  provider?: string | null;
  /** @nullable */
  modelId?: string | null;
  entries?: number;
  totalTokens?: number;
  totalCostUsd?: number;
  /** @nullable */
  averageDurationMs?: number | null;
  /** @nullable */
  maxDurationMs?: number | null;
}

/**
 * Cost of one usage type within one UTC hour. Only non-zero hours are returned.
 */
export interface LlmUsageHourlyCostDto {
  hourUtc?: string;
  usageType?: LlmUsageType;
  costUsd?: number;
}

export interface LlmUsageAdminSummaryDto {
  summary?: LlmUsageSummaryDto;
  /** @nullable */
  byUsageType?: LlmUsageTypeAggregateDto[] | null;
  /** @nullable */
  byCompany?: LlmUsageCompanyAggregateDto[] | null;
  /** @nullable */
  byUser?: LlmUsageUserAggregateDto[] | null;
  /** @nullable */
  byModel?: LlmUsageModelAggregateDto[] | null;
  /** @nullable */
  costByHour?: LlmUsageHourlyCostDto[] | null;
}

export interface LlmUsageCostDto {
  usageType?: LlmUsageType;
  scopeId?: string;
  /** @nullable */
  entryId?: string | null;
  userId?: number;
  companyId?: number;
  /** @nullable */
  provider?: string | null;
  /** @nullable */
  modelId?: string | null;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  totalCostUsd?: number;
  /** @nullable */
  stopReason?: string | null;
  createdAt?: string;
  /** @nullable */
  durationMs?: number | null;
  /** @nullable */
  companyName?: string | null;
  /** @nullable */
  userName?: string | null;
}

export interface LlmUsageTypeCostDto {
  usageType?: LlmUsageType;
  costUsd?: number;
}

/**
 * Every LLM call in one conversation, assistant session, recording or evaluation run.
 */
export interface LlmUsageScopeDto {
  scopeId?: string;
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
  userId?: number;
  /** @nullable */
  userName?: string | null;
  firstAt?: string;
  lastAt?: string;
  entries?: number;
  totalCostUsd?: number;
  /** @nullable */
  costByType?: LlmUsageTypeCostDto[] | null;
  /** @nullable */
  conversationId?: number | null;
}

export type LlmUsageScopeSort = (typeof LlmUsageScopeSort)[keyof typeof LlmUsageScopeSort];

export const LlmUsageScopeSort = {
  Cost: 'Cost',
  Entries: 'Entries',
  Started: 'Started'
} as const;

export interface ModelLedEvaluationStrategySettings {
  status?: EvaluationStrategyConfigStatus;
  /** @nullable */
  strategy?: string | null;
  /** @nullable */
  mode?: string | null;
  enabled?: boolean;
  dedupMode?: IssueDedupMode;
  /** @nullable */
  domainContext?: string | null;
  /** @nullable */
  exemptElementIds?: number[] | null;
  /** @nullable */
  criticalIssueTypes?: string[] | null;
  /** @nullable */
  catchAllElementId?: number | null;
  mentionCrossRefsInFeedback?: boolean;
  /** @nullable */
  availableIssueTypes?: IssueTaxonomyEntry[] | null;
}

export interface UpdateModelLedEvaluationStrategyCommand {
  enabled?: boolean;
  dedupMode?: IssueDedupMode;
  /** @nullable */
  domainContext?: string | null;
  /** @nullable */
  exemptElementIds?: number[] | null;
  /** @nullable */
  criticalIssueTypes?: string[] | null;
  /** @nullable */
  catchAllElementId?: number | null;
  mentionCrossRefsInFeedback?: boolean;
}

export interface ModelLedEvaluationStrategySimulationRequest {
  /** @nullable */
  conversationTranscript?: string | null;
  settings?: UpdateModelLedEvaluationStrategyCommand;
}

export interface ModelLedSimulationElement {
  id?: number;
  /** @nullable */
  name?: string | null;
  order?: number;
  categoryId?: number;
  /** @nullable */
  categoryName?: string | null;
  categoryOrder?: number;
}

export interface ModelLedSimulationIssue {
  /** @nullable */
  issueId?: string | null;
  /** @nullable */
  issueType?: string | null;
  /** @nullable */
  subtype?: string | null;
  /** @nullable */
  incidentKey?: string | null;
  /** @nullable */
  summary?: string | null;
  severity?: IssueSeverity;
  /** @nullable */
  evidence?: IssueEvidence[] | null;
  /** @nullable */
  candidateElements?: IssueCandidateElement[] | null;
  /** @nullable */
  links?: IssueLink[] | null;
  requiresLocalPolicy?: boolean;
  requiresScenarioGroundTruth?: boolean;
  affectedSafetyOrOutcome?: boolean;
  /** @nullable */
  allocatedElementId?: number | null;
  /** @nullable */
  unallocatedReason?: string | null;
  /** @nullable */
  crossReferencedElementIds?: number[] | null;
}

export interface ModelLedEvaluationStrategySimulationResult {
  /** @nullable */
  elements?: ModelLedSimulationElement[] | null;
  /** @nullable */
  issues?: ModelLedSimulationIssue[] | null;
  extractedIssueCount?: number;
  allocatedIssueCount?: number;
  unallocatedIssueCount?: number;
}

export interface MonitorEvaluationElement {
  /** @nullable */
  category?: string | null;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  elementType?: string | null;
  /** @nullable */
  score?: number | null;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  yesNo?: YesNoAnswer;
  /** @nullable */
  choice?: string | null;
  /** @nullable */
  text?: string | null;
  notApplicable?: boolean;
  /** @nullable */
  explanation?: string | null;
}

export interface MonitorEvaluation {
  id?: number;
  type?: EvaluationType;
  /** @nullable */
  protocolName?: string | null;
  /** @nullable */
  elements?: MonitorEvaluationElement[] | null;
}

export interface MonitorEvent {
  /** @nullable */
  eventType?: string | null;
  eventTime?: string;
}

export interface MonitorFeedback {
  emoji?: FeedbackEmoji;
  /** @nullable */
  text?: string | null;
  created?: string;
}

export interface MonitorScenarioVariable {
  /** @nullable */
  name?: string | null;
  /** @nullable */
  possibleValues?: string[] | null;
}

export interface MonitorScenarioPersona {
  scenarioPersonaId?: number;
  /** @nullable */
  personaName?: string | null;
  /** @nullable */
  personaDescription?: string | null;
  gender?: Gender;
  /** @nullable */
  roleName?: string | null;
  /** @nullable */
  roleDescription?: string | null;
}

export interface MonitorScenario {
  id?: number;
  versionId?: number;
  versionFound?: boolean;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  scenarioContextIntro?: string | null;
  /** @nullable */
  langCode: string | null;
  isLlmEnabled?: boolean;
  disableEndDetection?: boolean;
  /** @nullable */
  llm?: string | null;
  /** @nullable */
  contextFacts: string[] | null;
  /** @nullable */
  variables: MonitorScenarioVariable[] | null;
  /** @nullable */
  personas: MonitorScenarioPersona[] | null;
  blockCount?: number;
}

export interface MonitorSessionDetails {
  id?: number;
  sessionId?: string;
  scenarioId?: number;
  sceneVersionId?: number;
  /** @nullable */
  originalScenarioName?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  organizationName?: string | null;
  start?: string;
  /** @nullable */
  end?: string | null;
  /** @nullable */
  endReason?: string | null;
  isPhoneConversation?: boolean;
  /** @nullable */
  callSid?: string | null;
  hasUserAudio?: boolean;
  turnCount?: number;
  /** @nullable */
  totalEvaluationScore?: number | null;
  /** @nullable */
  summaryText?: string | null;
  /** @nullable */
  callRecordingUrl?: string | null;
}

export interface MonitorTurn {
  number?: number;
  start?: string;
  end?: string;
  /** @nullable */
  userSays?: string | null;
  /** @nullable */
  responseText?: string | null;
  isFallback?: boolean;
  /** @nullable */
  originalIntentName?: string | null;
  /** @nullable */
  selectedScenarioPersonaId?: number | null;
  /** @nullable */
  aiSpeakerName?: string | null;
  /** @nullable */
  aiHeardDurationMs?: number | null;
  hasUserAudio?: boolean;
  color?: QualityLevel;
}

export interface MonitorSessionBundle {
  session: MonitorSessionDetails;
  /** @nullable */
  turns: MonitorTurn[] | null;
  scenario?: MonitorScenario;
  /** @nullable */
  scenarioError?: string | null;
  /** @nullable */
  feedback: MonitorFeedback[] | null;
  /** @nullable */
  evaluations: MonitorEvaluation[] | null;
  /** @nullable */
  events: MonitorEvent[] | null;
}

export interface MonitorSessionSummary {
  id?: number;
  sessionId?: string;
  scenarioId?: number;
  sceneVersionId?: number;
  /** @nullable */
  originalScenarioName?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  organizationName?: string | null;
  start?: string;
  /** @nullable */
  end?: string | null;
  /** @nullable */
  endReason?: string | null;
  isPhoneConversation?: boolean;
  /** @nullable */
  callSid?: string | null;
  hasUserAudio?: boolean;
  turnCount?: number;
  /** @nullable */
  totalEvaluationScore?: number | null;
  /** @nullable */
  summaryText?: string | null;
}

export interface MonitorSessionPage {
  /** @nullable */
  items?: MonitorSessionSummary[] | null;
  nextAfterId?: number;
}

export interface MonthlyStatisticDto {
  month?: string;
  conversationCount?: number;
  averageScore?: number;
  uniqueUsers?: number;
}

export interface OperatorTurnResult {
  /** @nullable */
  text?: string | null;
  isEnd?: boolean;
  sendSms?: boolean;
  /**
   * Medical-index triage level for the call so far: 'red', 'yellow', 'green', or null.
   * @nullable
   */
  triageLevel?: string | null;
  /**
   * Short phrase for what the patient is most likely experiencing (Norwegian), or null.
   * @nullable
   */
  likelyCondition?: string | null;
  /**
   * Short Norwegian sentence for why the triage level is what it is, or null.
   * @nullable
   */
  triageReason?: string | null;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoJobStatusDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: JobStatusDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoLlmUsageCostDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: LlmUsageCostDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoLlmUsageScopeDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: LlmUsageScopeDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

/**
 * Compact persona summary returned from persona search.
 * Includes description text for search and selection flows.
 */
export interface PersonaSearchItemDto {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description: string | null;
  /** @nullable */
  languageCode: string | null;
  gender?: Gender;
  ttsProvider?: TtsProvider;
  /** @nullable */
  voiceName: string | null;
  /** @nullable */
  voiceId: string | null;
  /** @nullable */
  avatarUrl: string | null;
  otherCompaniesCount?: number;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoPersonaSearchItemDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: PersonaSearchItemDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

export type RecordingStatus = (typeof RecordingStatus)[keyof typeof RecordingStatus];

export const RecordingStatus = {
  Uploaded: 'Uploaded',
  Transcribing: 'Transcribing',
  Ready: 'Ready',
  Failed: 'Failed'
} as const;

export type RecordingEvaluationRunStatus =
  (typeof RecordingEvaluationRunStatus)[keyof typeof RecordingEvaluationRunStatus];

export const RecordingEvaluationRunStatus = {
  Queued: 'Queued',
  Evaluating: 'Evaluating',
  Ready: 'Ready',
  Failed: 'Failed'
} as const;

export interface RecordingListItemDto {
  id?: number;
  recordingGuid?: string;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  originalFileName?: string | null;
  fileSizeBytes?: number;
  /** @nullable */
  durationSeconds?: number | null;
  status?: RecordingStatus;
  createdUtc?: string;
  /** @nullable */
  callDateUtc?: string | null;
  /** @nullable */
  uploadedByUserId?: number | null;
  /** @nullable */
  uploadedByUserName?: string | null;
  /** @nullable */
  subjectUserId?: number | null;
  /** @nullable */
  subjectUserName?: string | null;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  departmentName?: string | null;
  hasAudio?: boolean;
  runCount?: number;
  /** @nullable */
  latestRunId?: number | null;
  latestRunStatus?: RecordingEvaluationRunStatus;
  /** @nullable */
  latestRunScore?: number | null;
  /** @nullable */
  latestRunProtocolNames?: string[] | null;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoRecordingListItemDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: RecordingListItemDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

export interface ScenarioSearchItemDto {
  id?: number;
  versionId?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  companyName?: string | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  langCode: string | null;
  isPublished?: boolean;
  isLlmEnabled?: boolean;
  /** @nullable */
  folderId?: number | null;
  /** @nullable */
  folderName?: string | null;
}

/**
 * Generic wrapper for paged API responses.
 */
export interface PagedResultDtoScenarioSearchItemDto {
  /**
   * Result items for the requested page.
   * @nullable
   */
  items: ScenarioSearchItemDto[] | null;
  /** Total number of items across all pages. */
  totalCount?: number;
}

export interface PartialListCourseDto {
  /** @nullable */
  data?: CourseDto[] | null;
  total?: number;
  skip?: number;
  page?: number;
}

export interface PartialListDialogStatisticDto {
  /** @nullable */
  data?: DialogStatisticDto[] | null;
  total?: number;
  skip?: number;
  page?: number;
}

export interface ScheduledCallDto {
  id?: number;
  scenarioId?: number;
  /** @nullable */
  scenarioName: string | null;
  scheduledTime?: string;
  /** @nullable */
  targetName: string | null;
  /** @nullable */
  userId?: number | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  phoneNumber: string | null;
  status?: ScheduledCallStatus;
  speechRecognitionTimeout?: number;
  isLlmEnabled?: boolean;
}

export interface PartialListScheduledCallDto {
  /** @nullable */
  data?: ScheduledCallDto[] | null;
  total?: number;
  skip?: number;
  page?: number;
}

export interface TenantDto {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  domain: string | null;
  /** @nullable */
  tenantId: string | null;
}

export interface UserDto {
  id?: number;
  role?: UserRole;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  phone?: string | null;
  companyId?: number;
  /** @nullable */
  companyName?: string | null;
  isAggreementAccepted?: boolean;
  hideScenarioContent?: boolean;
  speechThreshold?: number;
  speechWaitingTime?: number;
  smartTurnEnabled?: boolean;
  scenarioChatBargeInEnabled?: boolean;
  energyGateEnabled?: boolean;
  energyGateThresholdDb?: number;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  departmentName?: string | null;
  /** @nullable */
  externalOrganizationId?: string | null;
  /** @nullable */
  roleName?: string | null;
  tenant?: TenantDto;
  hasSklls?: boolean;
  hasInterpreter?: boolean;
  hasExercises?: boolean;
  hasRecordings?: boolean;
}

export interface UserGroupDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  /** @nullable */
  title: string | null;
  companyId?: number;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  departmentName?: string | null;
  /** @nullable */
  users?: UserDto[] | null;
}

export interface PartialListUserGroupDto {
  /** @nullable */
  data?: UserGroupDto[] | null;
  total?: number;
  skip?: number;
  page?: number;
}

export interface PersonaAudioResponse {
  /** @nullable */
  audio: string | null;
  /** @nullable */
  text: string | null;
}

export interface PersonaCompanyAccessDto {
  companyId?: number;
  access?: AccessLevel;
}

/**
 * DTO describing which companies still lack access to a persona for a given scenario.
 */
export interface PersonaCompanyAccessInfoDto {
  personaId?: number;
  /** @nullable */
  personaName: string | null;
  /** @nullable */
  missingCompanyIds?: number[] | null;
  /** @nullable */
  missingCompanyNames?: string[] | null;
}

export interface PersonaUsageScenarioDto {
  scenarioId?: number;
  /** @nullable */
  scenarioName?: string | null;
  /** @nullable */
  organizationId?: number | null;
  /** @nullable */
  organizationName?: string | null;
}

export interface PersonaDeletionValidationDto {
  canDelete?: boolean;
  /** @nullable */
  reasonIfCannot?: string | null;
  isSharedWithOtherOrganizations?: boolean;
  /** @nullable */
  scenariosUsingPersona?: PersonaUsageScenarioDto[] | null;
}

export interface PersonaSessionInitResult {
  sessionId?: string;
  /** @nullable */
  audio: string | null;
  /** @nullable */
  text: string | null;
}

/**
 * What one persona of a playbook item's scenario knows when the call starts.
 */
export interface PlaybookItemAiContextDto {
  personaId?: number;
  /** @nullable */
  personaName: string | null;
  /**
   * The text injected into the AI prompt. Empty when the exercise has shared memory off.
   * @nullable
   */
  context: string | null;
}

export interface PopularScenarioDto {
  /** @nullable */
  scenarioName?: string | null;
  /** @nullable */
  scenarioDescription?: string | null;
  usageCount?: number;
  /** @nullable */
  companyName?: string | null;
}

export interface ProblemDetails {
  /** @nullable */
  type?: string | null;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  status?: number | null;
  /** @nullable */
  detail?: string | null;
  /** @nullable */
  instance?: string | null;
  [key: string]: unknown;
}

export interface ProcessDialogCommand {
  sessionId?: string;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  speech?: string | null;
  /** @nullable */
  clientTurnId?: string | null;
  /** @nullable */
  interruptedAssistantTurnId?: string | null;
  /** @nullable */
  reconcileInterruptedAssistantTurn?: boolean | null;
  /** @nullable */
  previousAssistantHeardText?: string | null;
  /** @nullable */
  previousAssistantHeardFraction?: number | null;
  /** @nullable */
  previousAssistantHeardDurationMs?: number | null;
  /** @nullable */
  paceAudio?: string | null;
  backgroundContextKind?: BackgroundContextKind;
}

export interface ProcessResultDto {
  /** @nullable */
  sessionId?: string | null;
  lastBlock?: BlockDto;
  scenarioPersona?: ScenarioPersonaDto;
  prevBlock?: BlockDto;
  lastBlockId?: number;
  prevBlocKId?: number;
  lastBlockScenarioId?: number;
  prevBlockScenarioId?: number;
  /** @nullable */
  text?: string | null;
  scoresChanges?: number;
  scoresColor?: QualityLevel;
  previousColor?: QualityLevel;
  /** @nullable */
  originalIntentText?: string | null;
  /** @nullable */
  originalIntentId?: number | null;
  /** @nullable */
  originalIntentName?: string | null;
  isEnd?: boolean;
  isFallback?: boolean;
  intentHitTime?: string;
}

/**
 * Request for the simulation-only audio dialog turn: like ScenarioEditor.Api.Dto.Simulation.ProcessSimulatorDialogDto
 * but the AI patient's reply is synthesized to audio (TTS). `Speech` is the caller's optional
 * base64 audio recording. `BackgroundContext` is prior-knowledge handover injected into the AI
 * persona's prompt; deliberately kept off the normal scenario player/chat endpoints.
 */
export interface ProcessSimulatorAudioDialogDto {
  sessionId: string;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  speech?: string | null;
  /** @nullable */
  backgroundContext?: string | null;
}

/**
 * Request for the simulation-only streaming audio dialog turn. Like
 * ScenarioEditor.Api.Dto.Simulation.ProcessSimulatorAudioDialogDto but follows the two-phase turn flow: this POST
 * generates the AI patient's reply text and returns a ticketed turn that the caller plays back via
 * the streaming audio endpoints (`ProcessController.TurnAudio` / `TurnAudioStream`).
 * `ClientTurnId` is a caller-supplied idempotency key for the turn. `BackgroundContext` is
 * prior-knowledge handover injected into the AI persona's prompt; deliberately kept off the normal
 * scenario player/chat endpoints.
 */
export interface ProcessSimulatorAudioTurnDto {
  sessionId: string;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  speech?: string | null;
  /** @nullable */
  clientTurnId: string | null;
  /** @nullable */
  backgroundContext?: string | null;
}

export interface ProcessSimulatorDialogDto {
  sessionId: string;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  backgroundContext?: string | null;
}

/**
 * Short-lived, read-only link to the stored audio.
 */
export interface RecordingAudioLinkDto {
  /** @nullable */
  url?: string | null;
  expiresAtUtc?: string;
}

export type TranscriptSpeakerRole = (typeof TranscriptSpeakerRole)[keyof typeof TranscriptSpeakerRole];

export const TranscriptSpeakerRole = {
  Operator: 'Operator',
  Caller: 'Caller',
  Other: 'Other',
  Patient: 'Patient',
  System: 'System'
} as const;

export interface RecordingTranscriptTurnDto {
  startSeconds?: number;
  role?: TranscriptSpeakerRole;
  /** @nullable */
  text?: string | null;
}

export interface RecordingRunProtocolDto {
  evaluationProtocolId?: number;
  /** @nullable */
  evaluationProtocolName?: string | null;
  /**
   * Criteria from this protocol that the run evaluates.
   * @nullable
   */
  evaluationProtocolElementIds?: number[] | null;
  /**
   * Total criteria in the protocol today; null if the protocol was deleted.
   * @nullable
   */
  protocolElementCount?: number | null;
  /**
   * Normalised 0–1 for this protocol only. Null until the run is Ready.
   * @nullable
   */
  score?: number | null;
  evaluation?: EvaluationDto;
}

export interface RecordingEvaluationRunDto {
  id?: number;
  sessionId?: string;
  status?: RecordingEvaluationRunStatus;
  /** @nullable */
  errorMessage?: string | null;
  /**
   * Normalised 0–1 across all protocols in the run.
   * @nullable
   */
  totalEvaluationScore?: number | null;
  createdUtc?: string;
  /** @nullable */
  createdByUserId?: number | null;
  /** @nullable */
  completedUtc?: string | null;
  /**
   * All criteria in the run, across protocols. Useful to pre-select "run again".
   * @nullable
   */
  evaluationProtocolElementIds?: number[] | null;
  /** @nullable */
  protocols?: RecordingRunProtocolDto[] | null;
}

export interface RecordingDetailDto {
  id?: number;
  recordingGuid?: string;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  originalFileName?: string | null;
  /** @nullable */
  contentType?: string | null;
  fileSizeBytes?: number;
  /** @nullable */
  durationSeconds?: number | null;
  status?: RecordingStatus;
  /** @nullable */
  errorMessage?: string | null;
  createdUtc?: string;
  /** @nullable */
  callDateUtc?: string | null;
  /** @nullable */
  transcribedUtc?: string | null;
  companyId?: number;
  /** @nullable */
  departmentId?: number | null;
  /** @nullable */
  uploadedByUserId?: number | null;
  /** @nullable */
  subjectUserId?: number | null;
  keepAudio?: boolean;
  hasAudio?: boolean;
  /** @nullable */
  summaryText?: string | null;
  /** @nullable */
  languageCode?: string | null;
  /** @nullable */
  speakers?: TranscriptSpeakerRole[] | null;
  /** @nullable */
  turns?: RecordingTranscriptTurnDto[] | null;
  /**
   * Newest first.
   * @nullable
   */
  evaluationRuns?: RecordingEvaluationRunDto[] | null;
}

export interface RespondToUserTurnCommand {
  sessionId: string;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  speech?: string | null;
  /** @nullable */
  clientTurnId: string | null;
  /** @nullable */
  interruptedAssistantTurnId?: string | null;
  /** @nullable */
  previousAssistantHeardText?: string | null;
  /** @nullable */
  previousAssistantHeardFraction?: number | null;
  /** @nullable */
  previousAssistantHeardDurationMs?: number | null;
}

export interface RespondToUserTurnDto {
  turnId: string;
  /** @nullable */
  clientTurnId: string | null;
  sessionId: string;
  /** @nullable */
  finalText: string | null;
  /** @nullable */
  lastBlockId?: number | null;
  isEnd?: boolean;
  audioExpected?: boolean;
  /** @nullable */
  audioUrl?: string | null;
  /** @nullable */
  audioStreamUrl?: string | null;
  /** @nullable */
  audioStreamFormat?: string | null;
  /** @nullable */
  audioTicket?: string | null;
  /** @nullable */
  audioTicketHeaderName?: string | null;
  persona: ScenarioPersonaDto;
  /** @nullable */
  avatarUrl?: string | null;
  userCountingPace?: UserCountingPaceDto;
}

export interface RespondWithPersonaAudioCommand {
  sessionId?: string;
  /** @nullable */
  text?: string | null;
}

export interface RestoreScenarioVersionCommand {
  scenarioId?: number;
  versionId?: number;
}

export interface RoleCompanyDto {
  id?: number;
  companyId?: number;
  access?: AccessLevel;
}

export type ScenarioChangeType = (typeof ScenarioChangeType)[keyof typeof ScenarioChangeType];

export const ScenarioChangeType = {
  ScenarioCreated: 'ScenarioCreated',
  ScenarioCopied: 'ScenarioCopied',
  ScenarioConvertedToShared: 'ScenarioConvertedToShared',
  ScenarioConvertedToUnshared: 'ScenarioConvertedToUnshared',
  ScenarioSharedWithCompany: 'ScenarioSharedWithCompany',
  ScenarioUnsharedWithCompany: 'ScenarioUnsharedWithCompany',
  ScenarioVersionRestored: 'ScenarioVersionRestored',
  ScenarioNameChanged: 'ScenarioNameChanged',
  ScenarioDescriptionChanged: 'ScenarioDescriptionChanged',
  ScenarioVoiceChanged: 'ScenarioVoiceChanged',
  SttProviderChanged: 'SttProviderChanged',
  LanguageChanged: 'LanguageChanged',
  IntroAudioChanged: 'IntroAudioChanged',
  IntentProcessingProviderChanged: 'IntentProcessingProviderChanged',
  LlmEnablingChanged: 'LlmEnablingChanged',
  LlmChanged: 'LlmChanged',
  ScenarioContextFactAdded: 'ScenarioContextFactAdded',
  ScenarioContextFactChanged: 'ScenarioContextFactChanged',
  ScenarioContextFactDeleted: 'ScenarioContextFactDeleted',
  ScenarioContextPersonaAdded: 'ScenarioContextPersonaAdded',
  ScenarioContextPersonaChanged: 'ScenarioContextPersonaChanged',
  ScenarioContextPersonaDeleted: 'ScenarioContextPersonaDeleted',
  PersonasChanged: 'PersonasChanged',
  BlockAdded: 'BlockAdded',
  BlockNameChanged: 'BlockNameChanged',
  BlockTypeChanged: 'BlockTypeChanged',
  BlockDescriptionChanged: 'BlockDescriptionChanged',
  BlockQualityLevelChanged: 'BlockQualityLevelChanged',
  BlockDeleted: 'BlockDeleted',
  BlockLinkAdded: 'BlockLinkAdded',
  BlockLinkDeleted: 'BlockLinkDeleted',
  BlockPersonaChanged: 'BlockPersonaChanged',
  AiResponseAdded: 'AiResponseAdded',
  AiResponseChanged: 'AiResponseChanged',
  AiResponseEmotionChanged: 'AiResponseEmotionChanged',
  AiResponseDeleted: 'AiResponseDeleted',
  IntentPhraseAdded: 'IntentPhraseAdded',
  IntentPhraseChanged: 'IntentPhraseChanged',
  IntentPhraseDeleted: 'IntentPhraseDeleted',
  GlobalIntentAdded: 'GlobalIntentAdded',
  GlobalIntentRemoved: 'GlobalIntentRemoved',
  MaxTokensChanged: 'MaxTokensChanged',
  VariableAdded: 'VariableAdded',
  VariableRemoved: 'VariableRemoved',
  VariableChanged: 'VariableChanged',
  VariableValueAdded: 'VariableValueAdded',
  VariableValueRemoved: 'VariableValueRemoved',
  VariableValueChanged: 'VariableValueChanged',
  CompanyVariableAdded: 'CompanyVariableAdded',
  CompanyVariableRemoved: 'CompanyVariableRemoved',
  CompanyVariableChanged: 'CompanyVariableChanged',
  IsPublishedChanged: 'IsPublishedChanged',
  IsDecisionSupportEnabledChanged: 'IsDecisionSupportEnabledChanged',
  NotesEnabledChanged: 'NotesEnabledChanged',
  DisableEndDetectionChanged: 'DisableEndDetectionChanged',
  NotesEvaluationProtocolElementsChanged: 'NotesEvaluationProtocolElementsChanged',
  EvaluationProtocolElementsChanged: 'EvaluationProtocolElementsChanged',
  SpeedChanged: 'SpeedChanged',
  PaceCountingChanged: 'PaceCountingChanged',
  EmotionalVoiceChanged: 'EmotionalVoiceChanged',
  BackgroundSoundChanged: 'BackgroundSoundChanged'
} as const;

export interface ScenarioChatTurnStatusDto {
  committed?: boolean;
  /** @nullable */
  turnId?: string | null;
  isEnd?: boolean;
}

export interface ScenarioDialogStatisticDto {
  sex?: Gender;
  /** @nullable */
  voiceName?: string | null;
  /** @nullable */
  voiceId?: string | null;
  /** @nullable */
  langCode?: string | null;
  ttsProvider?: TtsProvider;
}

export interface ScenarioHealthCallsDto {
  last30Days?: number;
  last90Days?: number;
  last180Days?: number;
  last365Days?: number;
  total?: number;
}

/**
 * @nullable
 */
export type ScenarioHealthFindingDtoValues = { [key: string]: string } | null;

export interface ScenarioHealthFindingDto {
  rule?: HealthRule;
  severity?: HealthSeverity;
  points?: number;
  /** @nullable */
  values?: ScenarioHealthFindingDtoValues;
  /** @nullable */
  names?: string[] | null;
  /** @nullable */
  personaId?: number | null;
  /** @nullable */
  variableName?: string | null;
}

export interface ScenarioHealthRuleDto {
  rule?: HealthRule;
  severity?: HealthSeverity;
  points?: number;
}

export interface ScenarioHealthRowDto {
  scenarioId?: number;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  organizationId?: number | null;
  /** @nullable */
  organizationName?: string | null;
  /** @nullable */
  languageCode?: string | null;
  isPublished?: boolean;
  isLlmEnabled?: boolean;
  updatedAt?: string;
  personaCount?: number;
  /** @nullable */
  llmLabel?: string | null;
  calls?: ScenarioHealthCallsDto;
  isEmpty?: boolean;
  /** @nullable */
  score?: number | null;
  /** @nullable */
  findings?: ScenarioHealthFindingDto[] | null;
}

export interface ScenarioHealthReportDto {
  checkedAt?: string;
  /** @nullable */
  recommendedLlmLabel?: string | null;
  maxPersonas?: number;
  /** @nullable */
  rules?: ScenarioHealthRuleDto[] | null;
  /** @nullable */
  scenarios?: ScenarioHealthRowDto[] | null;
}

export interface ScenarioNoteDto {
  id?: number;
  sessionId?: string;
  /** @nullable */
  notes?: string | null;
  /** @nullable */
  submittedAt?: string | null;
}

export interface ScenarioNoteRequest {
  /**
   * @minLength 1
   * @maxLength 10000
   */
  notes: string;
}

export interface ScenarioUpdateDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  sceneId?: number;
  versionId?: number;
  scenarioChangeType?: ScenarioChangeType;
  /** @nullable */
  scenarioChangeTypeName?: string | null;
  /** @nullable */
  oldValue?: string | null;
  /** @nullable */
  newValue?: string | null;
  /** @nullable */
  blockName?: string | null;
}

export interface ScheduledCourseAssignmentCommand {
  id?: number;
}

export interface ScheduledCourseNotificationDto {
  id?: number;
  /** @nullable */
  createdBy?: number | null;
  /** @nullable */
  createdByUserName?: string | null;
  createdDate?: string;
  /** @nullable */
  modifiedByUserName?: string | null;
  date?: string;
  /** @nullable */
  customText?: string | null;
}

export interface ScheduledCourseScenarioResultDto {
  scenario: ScenarioDto;
  dialogStatistic: DialogStatisticDto;
  /** @nullable */
  status?: string | null;
}

export interface ShareEvaluationProtocolCommand {
  evaluationProtocolId?: number;
  /** @nullable */
  companies?: ProtocolCompanyAccessDto[] | null;
}

export interface ShareRoleDto {
  companyId: number;
  accessLevel?: AccessLevel;
}

export interface SimpleCompanyDto {
  id?: number;
  /** @nullable */
  name?: string | null;
}

export interface SimpleScenarioDto {
  id?: number;
  versionId?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  companyName?: string | null;
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  langCode?: string | null;
  /** @nullable */
  sceneGroupId?: number | null;
  /** @nullable */
  sharedCompanies?: SimpleCompanyDto[] | null;
}

export interface SimulationStageDto {
  id?: number;
  simulationId?: number;
  sortOrder?: number;
  /** @nullable */
  name: string | null;
  scenarioSideAId?: number;
  /**
   * SideB scenario, or null when SideB is driven by the agent engine (the operator side);
   * in that case the stage's ScenarioEditor.Api.Dto.Simulation.SimulationStageDto.Name role key identifies which agent.
   * @nullable
   */
  scenarioSideBId?: number | null;
  /** @nullable */
  sideASessionId?: string | null;
  /** @nullable */
  sideBSessionId?: string | null;
  /** @nullable */
  startedDateTime?: string | null;
  /** @nullable */
  endedDateTime?: string | null;
  /**
   * Conversation ids resolved from the session ids, for deep-linking to the
   * Conversations view. Only populated on the detail endpoint, and only once
   * the side's conversation has been persisted.
   * @nullable
   */
  sideADialogStatisticId?: number | null;
  /** @nullable */
  sideBDialogStatisticId?: number | null;
}

export interface SimulationDto {
  id?: number;
  companyId?: number;
  /** @nullable */
  title: string | null;
  /** @nullable */
  description?: string | null;
  /**
   * The effective lifecycle status: Draft, Ready, Running or Completed.
   * @nullable
   */
  status: string | null;
  /** @nullable */
  startedDateTime?: string | null;
  /** @nullable */
  endedDateTime?: string | null;
  /**
   * The patient journey aggregate id (VITAL contract `caseId`); null until started.
   * @nullable
   */
  caseId?: string | null;
  /**
   * The single patient dialog session kept open for the whole journey; null until started.
   * @nullable
   */
  patientSessionId?: string | null;
  /** True once an ambulance has been dispatched for this run. */
  ambulanceDispatched?: boolean;
  /**
   * When the ambulance was dispatched, or null if not yet dispatched.
   * @nullable
   */
  ambulanceDispatchedDateTime?: string | null;
  /**
   * The dispatched unit identifier, if one was supplied.
   * @nullable
   */
  ambulanceUnit?: string | null;
  /**
   * The dispatch ETA in seconds, if one was supplied.
   * @nullable
   */
  ambulanceEtaSeconds?: number | null;
  /** True once the ambulance has arrived at the patient this run. */
  ambulanceArrived?: boolean;
  /**
   * When the world-sim reported the ambulance arrived, or null while still en route.
   * @nullable
   */
  ambulanceArrivedDateTime?: string | null;
  modifiedDate?: string;
  /** @nullable */
  stages?: SimulationStageDto[] | null;
}

export interface Speaker {
  /** @nullable */
  id: string | null;
  ttsProvider?: TtsProvider;
  /** @nullable */
  languageCode: string | null;
  /** @nullable */
  name: string | null;
  sex?: Gender;
}

export interface StartOperatorSessionResult {
  sessionId?: string;
  /** @nullable */
  text?: string | null;
}

export interface SummaryDto {
  simulationId?: number;
  /** @nullable */
  summaryText?: string | null;
  /**
   * The dialog session whose turns were last folded into the memory (null if none yet).
   * @nullable
   */
  lastSessionId?: string | null;
  lastTurnNumber?: number;
  /** @nullable */
  updatedAt?: string | null;
  /**
   * True when this request produced a new memory; false when there were no new turns,
   * or for a plain read.
   */
  updated?: boolean;
}

export interface SummaryRequestDto {
  simulationId: number;
  sessionId: string;
}

export interface SynthesizeSpeechCommand {
  /** @nullable */
  intentId?: number | null;
  service?: TtsProvider;
  sex?: Gender;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  language?: string | null;
  /** @nullable */
  voiceName?: string | null;
  speed?: number;
  /** @nullable */
  voiceId?: string | null;
  /** @nullable */
  scenarioId?: number | null;
  /** @nullable */
  scenarioPersonaId?: number | null;
  /** @nullable */
  scenarioVersionId?: number | null;
  pacing?: CountingPacing;
  /** @nullable */
  breathingSeed?: number | null;
}

export interface TaskTemplateDto {
  /** @nullable */
  taskType?: string | null;
  payloadTemplate?: unknown;
}

export interface TestExceptionRequest {
  /** @nullable */
  message?: string | null;
}

export interface TextToSpeechCommand {
  /** @nullable */
  callSid?: string | null;
  /** @nullable */
  text?: string | null;
  /** @nullable */
  clientTurnId?: string | null;
  /** @nullable */
  interruptedAssistantClientTurnId?: string | null;
  /** @nullable */
  previousAssistantHeardDurationMs?: number | null;
  /** @nullable */
  previousAssistantTotalDurationMs?: number | null;
  /** @nullable */
  audio?: string | null;
}

export interface TrendingScenariosCommand {
  companyId?: number;
  count?: number;
}

export interface TriggerJobRequest {
  /** @nullable */
  taskType: string | null;
  payload: unknown;
}

export interface TwilioPhoneNumberDto {
  /** @nullable */
  phoneNumber: string | null;
  /** @nullable */
  friendlyName: string | null;
  /** Whether a call to this number reaches this environment's inbound exercise handling. */
  routesToSklls?: boolean;
}

export interface UnshareEvaluationProtocolCommand {
  evaluationProtocolId?: number;
  /** @nullable */
  companyIds?: number[] | null;
}

export interface UnshareRoleDto {
  companyId: number;
}

export interface UpdateCompanyCommand {
  id?: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  users?: User[] | null;
  /** @nullable */
  departments?: CompanyDepartment[] | null;
  /** @nullable */
  scenarios?: Scenario[] | null;
  /** @nullable */
  scenarioGroups?: ScenarioGroup[] | null;
  /** @nullable */
  sharedScenarios?: Scenario[] | null;
  /** @nullable */
  sharedGlobalAssetCategories?: GlobalAssetCategory[] | null;
  /** @nullable */
  globalAssetCategories?: GlobalAssetCategory[] | null;
  /** @nullable */
  readonly externalName?: string | null;
  /** @nullable */
  groups?: Group[] | null;
  /** @nullable */
  tenants?: Tenant[] | null;
  /** @nullable */
  defaultLanguage?: string | null;
  enableElevenLabsV3?: boolean;
  /** @nullable */
  licenseAgreements?: LicenseAgreement[] | null;
  /** @nullable */
  evaluationProtocolCompanies?: EvaluationProtocolCompany[] | null;
  /** @nullable */
  personaCompanies?: PersonaCompany[] | null;
  /** @nullable */
  roleCompanies?: RoleCompany[] | null;
}

export interface UpdateCourseCommand {
  id?: number;
  /** @nullable */
  title?: string | null;
}

export interface UpdateEvaluationProtocolCategoryCommand {
  id?: number;
  /** @nullable */
  name?: string | null;
}

export interface UpdateEvaluationProtocolCommand {
  id?: number;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  languageCode?: string | null;
  /** @nullable */
  sharedInstructions?: string | null;
}

export interface UpdateEvaluationProtocolCompanyAccessCommand {
  evaluationProtocolId?: number;
  companyId?: number;
  access?: AccessLevel;
}

export interface UpdateEvaluationProtocolElementCommand {
  id?: number;
  evaluationProtocolCategoryId?: number;
  /** @nullable */
  name?: string | null;
  type?: EvaluationProtocolElementType;
  /** @nullable */
  scaleMin?: number | null;
  /** @nullable */
  scaleMax?: number | null;
  /** @nullable */
  choices?: string | null;
  explanationEnabled?: boolean;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  allowNotApplicable?: boolean | null;
  /** @nullable */
  allowMultiple?: boolean | null;
  /** @nullable */
  goalSeconds?: number | null;
  /** @nullable */
  excludedFromScore?: boolean | null;
}

export interface UpdateExerciseContactDto {
  id: number;
  /**
   * @minLength 0
   * @maxLength 200
   */
  name: string;
  /**
   * @minLength 0
   * @maxLength 50
   */
  phoneNumber: string;
  /** @minLength 1 */
  type: string;
  /**
   * Linked Sklls user for a Human contact; null for manual entry and AI contacts.
   * @nullable
   */
  userId?: number | null;
}

export interface UpdateExerciseDto {
  id: number;
  /**
   * @minLength 0
   * @maxLength 200
   */
  title: string;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * Background given to every AI caller when shared memory is on. Null keeps the stored text; empty clears it.
   * @minLength 0
   * @maxLength 4000
   * @nullable
   */
  aiBriefing?: string | null;
  /**
   * Null keeps the stored value.
   * @nullable
   */
  sharedMemoryEnabled?: boolean | null;
  scheduledDateTime: string;
  scheduledEndDateTime: string;
  /** @nullable */
  startedDateTime?: string | null;
  /** @nullable */
  endedDateTime?: string | null;
}

export interface UpdateGlobalAssetCategoryCommand {
  /** @nullable */
  title?: string | null;
}

export interface UpdateGlobalAssetIntentCommand {
  /** @nullable */
  title?: string | null;
}

export interface UpdateLicenseAgreementDto {
  id: number;
  companyId: number;
  /** @nullable */
  departmentId?: number | null;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  skllsSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  interpreterSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  exercisesSeats: number;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  recordingsSeats: number;
  startTime: string;
  expiryTime: string;
}

/**
 * DTO for updating an existing persona.
 */
export interface UpdatePersonaDto {
  /** The ID of the persona to update. */
  id: number;
  /**
   * The name of the persona.
   * @minLength 0
   * @maxLength 100
   */
  name: string;
  /**
   * Optional description of the persona. Behavioral personas (e.g. agonal breathing patients)
   * carry their full behavioral contract here and need far more than the template's 500 characters.
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * The language code for the persona (e.g., "en-US", "sv-SE").
   * @minLength 0
   * @maxLength 10
   */
  languageCode: string;
  gender: Gender;
  ttsProvider: TtsProvider;
  /**
   * The voice name to use with the TTS provider. Deprecated; prefer voiceId.
   * @minLength 0
   * @maxLength 100
   */
  voiceName: string;
  /**
   * Provider-stable voice identifier (e.g., Azure ShortName, Google voice name, ElevenLabs voice_id).
   * @minLength 0
   * @maxLength 100
   * @nullable
   */
  voiceId?: string | null;
  /**
   * Optional URL to an avatar image for the persona.
   * @minLength 0
   * @maxLength 500
   * @nullable
   */
  avatarUrl?: string | null;
}

export interface UpdatePlaybookItemDto {
  id: number;
  scenarioId: number;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * Personas outside the item's scenario that also know what happened in its calls. Null keeps the stored list.
   * @nullable
   */
  knownPersonaIds?: number[] | null;
  scheduledDateTime: string;
  /** @nullable */
  endedDateTime?: string | null;
  /** @nullable */
  fromExerciseContactId?: number | null;
  toExerciseContactId: number;
}

export interface UpdateRecordingRequest {
  /** @nullable */
  title?: string | null;
  /** @nullable */
  subjectUserId?: number | null;
  /**
   * Set true to remove the subject user. Ignored when SubjectUserId is given.
   * @nullable
   */
  clearSubjectUser?: boolean | null;
  /** @nullable */
  callDateUtc?: string | null;
  /**
   * Set true to remove the call date. Ignored when CallDateUtc is given.
   * @nullable
   */
  clearCallDate?: boolean | null;
}

export interface UpdateRoleDto {
  /** @nullable */
  name: string | null;
  /** @nullable */
  description?: string | null;
}

export interface UpdateScenarioContentCommand {
  id?: number;
  centerX?: number;
  centerY?: number;
  scale?: number;
  /** @nullable */
  blocks?: AddBlockCommand[] | null;
  /** @nullable */
  blockLinks?: AddBlockLinkCommand[] | null;
}

export interface UpdateScenarioGroupCommand {
  /** @nullable */
  title?: string | null;
}

export interface VariableValueSettingsDto {
  /** @nullable */
  id?: number | null;
  /** @nullable */
  value?: string | null;
  /** @nullable */
  companyId?: number | null;
}

export interface VariableSettingsDto {
  /** @nullable */
  id?: number | null;
  /** @nullable */
  name?: string | null;
  /** @nullable */
  values?: VariableValueSettingsDto[] | null;
  /** @nullable */
  deletedValueIds?: number[] | null;
}

export interface UpdateScenarioSettingsCommand {
  id?: number;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  name?: string | null;
  speed?: number;
  /** @nullable */
  langCode?: string | null;
  /** @nullable */
  evaluationProtocolElements?: EvaluationProtocolElement[] | null;
  /** @nullable */
  scenarioPersonas?: ScenarioPersona[] | null;
  /** @nullable */
  scenarioContextIntro?: string | null;
  playScenarioContextOn?: boolean;
  context?: ScenarioContext;
  isLlmEnabled?: boolean;
  /** @nullable */
  description?: string | null;
  llm?: Llm;
  isDecisionSupportEnabled?: boolean;
  notesEnabled?: boolean;
  disableEndDetection?: boolean;
  paceCountingEnabled?: boolean;
  /** @nullable */
  defaultCountingBpm?: number | null;
  /** @nullable */
  notesEvaluationProtocolElements?: EvaluationProtocolElement[] | null;
  maxTokens?: number;
  isPublished?: boolean;
  /** @nullable */
  variables?: VariableSettingsDto[] | null;
  /** @nullable */
  emotionalVoiceEnabled?: boolean | null;
  /** @nullable */
  backgroundSoundId?: number | null;
  /** @nullable */
  backgroundSoundVolume?: number | null;
  clearBackgroundSound?: boolean;
}

export interface UpdateScheduledCallCommand {
  scenarioId?: number;
  scheduledTime?: string;
  /** @nullable */
  targetName?: string | null;
  /** @nullable */
  userId?: number | null;
  /** @nullable */
  phoneNumber?: string | null;
  isLlmEnabled?: boolean;
  speechRecognitionTimeout?: number;
  isDigitsGatherEnabled?: boolean;
}

export interface UpdateScheduledCourseNotificationCommand {
  id?: number;
  date?: string;
  /** @nullable */
  customText?: string | null;
}

export interface UpdateScheduledCourseRequestCommand {
  /** @nullable */
  departmentId?: number | null;
  startDate?: string;
  endDate?: string;
}

export interface UpdateSimulationStageDto {
  /** Existing stage id, or 0 for a new stage. Stages omitted from the list are deleted. */
  id?: number;
  sortOrder?: number;
  /**
   * @minLength 0
   * @maxLength 200
   */
  name: string;
  scenarioSideAId: number;
  /**
   * SideB scenario, or null when SideB is driven by the agent engine (the operator side).
   * @nullable
   */
  scenarioSideBId?: number | null;
}

export interface UpdateSimulationDto {
  id: number;
  /**
   * @minLength 0
   * @maxLength 200
   */
  title: string;
  /**
   * @minLength 0
   * @maxLength 2000
   * @nullable
   */
  description?: string | null;
  /**
   * User-controlled readiness: Draft or Ready.
   * @minLength 1
   */
  status: string;
  /** @nullable */
  stages?: UpdateSimulationStageDto[] | null;
}

/**
 * Run state reported by the frontend orchestrator: the dialog session ids it started
 * for the stage's two sides and the stage's start/end timestamps.
 */
export interface UpdateSimulationStageRunStateDto {
  /** @nullable */
  sideASessionId?: string | null;
  /** @nullable */
  sideBSessionId?: string | null;
  /** @nullable */
  startedDateTime?: string | null;
  /** @nullable */
  endedDateTime?: string | null;
}

export interface UpdateUserGroupCommand {
  /** @nullable */
  title?: string | null;
}

export interface UpdateUsersAdvancedSettingsCommand {
  /** @nullable */
  ids?: number[] | null;
  hideScenarioContent?: boolean;
}

export interface UpdateUsersDepartmentCommand {
  /** @nullable */
  ids?: number[] | null;
  /** @nullable */
  departmentId?: number | null;
}

export interface UpdateUsersLicenseFeaturesCommand {
  /**
   * @minItems 1
   * @nullable
   */
  ids?: number[] | null;
  /** @nullable */
  hasSklls?: boolean | null;
  /** @nullable */
  hasInterpreter?: boolean | null;
  /** @nullable */
  hasExercises?: boolean | null;
  /** @nullable */
  hasRecordings?: boolean | null;
}

export interface UsageStatistic {
  month?: string;
  /** @nullable */
  companyName: string | null;
  scenarioRuns?: number;
}

export interface UsageStatisticsOverviewDto {
  totalConversations?: number;
  phoneConversation?: number;
  webConversation?: number;
}

export interface UserConversationCountDto {
  /** @nullable */
  userName?: string | null;
  conversationCount?: number;
  /** @nullable */
  departmentName?: string | null;
  /** @nullable */
  companyName?: string | null;
}

export interface UsageStatisticsDto {
  overview?: UsageStatisticsOverviewDto;
  /** @nullable */
  monthlyBreakdown?: MonthlyStatisticDto[] | null;
  /** @nullable */
  topUsers?: UserConversationCountDto[] | null;
  /** @nullable */
  popularScenarios?: PopularScenarioDto[] | null;
}

export interface UsageStatisticsQuery {
  /** @nullable */
  from?: string | null;
  /** @nullable */
  to?: string | null;
  /** @nullable */
  companyId?: number | null;
  /** @nullable */
  departmentIds?: number[] | null;
  /** @nullable */
  userIds?: number[] | null;
  /** @nullable */
  scenarioIds?: number[] | null;
  userRole?: UserRole;
  /** @nullable */
  isPhoneConversation?: boolean | null;
}

export type BackgroundSoundsUploadBodyOne = {
  File: Blob | File;
  Name: string;
};

export type BackgroundSoundsUploadBodyTwo = {
  File: Blob;
  Name: string;
};

export type CompaniesFilterCompaniesParams = {
  query?: string;
  skip?: number;
  take?: number;
};

export type CompaniesSearchAgentCompaniesParams = {
  query?: string;
  skip?: number;
  take?: number;
};

export type CourseGetParams = {
  companyId?: number;
  skip?: number;
  take?: number;
};

export type CourseGetUserScheduledCourseResultsParams = {
  userId?: number;
};

export type DialogSessionInitSessionParams = {
  versionId?: number;
};

export type DialogStatisticsListDialogStatisticsServiceParams = {
  Skip?: number;
  Take?: number;
  SortOrder?: SortType;
  OrderBy?: string;
  From?: string;
  To?: string;
  Search?: string;
  CompanyName?: string;
  DepartmentName?: string;
  ScenarioName?: string;
  UserName?: string;
  DurationMin?: number;
  DurationMax?: number;
  ScoreMin?: number;
  ScoreMax?: number;
  IsPhoneConversation?: boolean;
  selectedId?: number;
};

export type GlobalAssetsCopyIntentToCompanyParams = {
  companyId?: number;
};

export type GlobalAssetsImportDataBodyOne = {
  file?: Blob | File;
};

export type GlobalAssetsImportDataBodyTwo = {
  file?: Blob;
};

export type HuginAssistantChatsAdminListSessionsParams = {
  companyId?: number;
  userId?: number;
  take?: number;
};

export type JobStatusGetRecentJobsParams = {
  /**
   * The maximum number of recent jobs to return. Defaults to 100.
   */
  count?: number;
};

export type JobStatusGetRecentJobsPagedParams = {
  /**
   * 1-based page index
   */
  pageNumber?: number;
  /**
   * items per page
   */
  pageSize?: number;
  /**
   * Optional list of statuses to filter
   */
  statuses?: string[];
  /**
   * Optional list of task types to filter
   */
  taskTypes?: string[];
  /**
   * Optional search text for JobId or error message
   */
  searchText?: string;
  /**
   * Optional topic (LavinMQ stream name) to filter by
   */
  topic?: string;
};

export type LicenseAgreementsGetUsageParams = {
  companyId?: number;
  departmentId?: number;
};

export type LlmUsageGetAdminSummaryParams = {
  usageType?: LlmUsageType;
  companyId?: number;
  userId?: number;
  from?: string;
  to?: string;
};

export type LlmUsageGetAdminScopesParams = {
  usageType?: LlmUsageType;
  companyId?: number;
  userId?: number;
  from?: string;
  to?: string;
  search?: string;
  sort?: LlmUsageScopeSort;
  descending?: boolean;
  page?: number;
  pageSize?: number;
};

export type LlmUsageGetAdminRowsParams = {
  usageType?: LlmUsageType;
  companyId?: number;
  userId?: number;
  from?: string;
  to?: string;
  scopeId?: string;
  page?: number;
  pageSize?: number;
};

export type MonitorGetSessionsParams = {
  /**
   * Exclusive cursor; 0 starts from the beginning.
   */
  afterId?: number;
  /**
   * Page size, default 50, clamped to 1..200.
   */
  limit?: number;
  /**
   * Optional UTC lower bound on End (inclusive). Must be within the last 7 days, otherwise 400.
   */
  endedAfter?: string;
};

export type PersonaSearchPersonasParams = {
  /**
   * Optional free-text match against persona name, description, or voice name.
   */
  query?: string;
  /**
   * Optional language code filter.
   */
  languageCode?: string;
  /**
   * Optional TTS provider filter.
   */
  ttsProvider?: TtsProvider;
  /**
   * Number of matching personas to skip.
   */
  skip?: number;
  /**
   * Maximum number of personas to return.
   */
  take?: number;
};

export type ProcessScenarioChatTurnStatusParams = {
  sessionId?: string;
  clientTurnId?: string;
};

export type ProcessTurnAudioParams = {
  sessionId?: string;
  audioTicket?: string;
};

export type ProcessTurnAudioStreamParams = {
  sessionId?: string;
  audioTicket?: string;
};

export type ProcessStartInboundCallBodyOne = {
  AccountSid?: string;
  ApiVersion?: string;
  CallSid: string;
  CallStatus?: string;
  Direction?: string;
  From?: string;
  FromCity?: string;
  FromCountry?: string;
  FromState?: string;
  FromZip?: string;
  Caller?: string;
  CallerCity?: string;
  CallerCountry?: string;
  CallerState?: string;
  CallerZip?: string;
  To?: string;
  ToCity?: string;
  ToCountry?: string;
  ToState?: string;
  ToZip?: string;
  Called?: string;
  CalledCity?: string;
  CalledCountry?: string;
  CalledState?: string;
  CalledZip?: string;
};

export type ProcessStartInboundCallBodyTwo = {
  AccountSid?: string;
  ApiVersion?: string;
  CallSid: string;
  CallStatus?: string;
  Direction?: string;
  From?: string;
  FromCity?: string;
  FromCountry?: string;
  FromState?: string;
  FromZip?: string;
  Caller?: string;
  CallerCity?: string;
  CallerCountry?: string;
  CallerState?: string;
  CallerZip?: string;
  To?: string;
  ToCity?: string;
  ToCountry?: string;
  ToState?: string;
  ToZip?: string;
  Called?: string;
  CalledCity?: string;
  CalledCountry?: string;
  CalledState?: string;
  CalledZip?: string;
};

export type RecordingsUploadBodyOne = {
  File: Blob | File;
  Title: string;
  /** Keep the audio after transcription. Default: delete it. */
  KeepAudio?: boolean;
  AnonymizePersonalData?: boolean;
  /** The person who handled the call, optional. */
  SubjectUserId?: number;
  /**
   * Length of the audio in whole seconds, measured by the client from the file itself. The transcription model
   * only estimates duration and drifts badly on long calls, so a measured value is never overwritten by it.
   */
  DurationSeconds?: number;
  /** When the call took place, optional. Reports date the call by this, else by the upload time. */
  CallDateUtc?: string;
  /** Criteria (evaluation protocol element ids) for the first evaluation run. Empty means transcribe only. */
  EvaluationProtocolElementIds?: number[];
};

export type RecordingsUploadBodyTwo = {
  File: Blob;
  Title: string;
  /** Keep the audio after transcription. Default: delete it. */
  KeepAudio?: boolean;
  AnonymizePersonalData?: boolean;
  /** The person who handled the call, optional. */
  SubjectUserId?: number;
  /**
   * Length of the audio in whole seconds, measured by the client from the file itself. The transcription model
   * only estimates duration and drifts badly on long calls, so a measured value is never overwritten by it.
   */
  DurationSeconds?: number;
  /** When the call took place, optional. Reports date the call by this, else by the upload time. */
  CallDateUtc?: string;
  /** Criteria (evaluation protocol element ids) for the first evaluation run. Empty means transcribe only. */
  EvaluationProtocolElementIds?: number[];
};

export type RecordingsListParams = {
  search?: string;
  subjectUserId?: number;
  ids?: number[];
  page?: number;
  pageSize?: number;
};

export type RolesGetRolesParams = {
  langCode?: string;
  companyId?: number;
};

export type ScenarioGroupCreateGroupParams = {
  companyId?: number;
};

export type ScenarioGroupAddScenarioToGroupParams = {
  sceneIds?: number[];
};

export type ScenarioGroupRemoveScenesFromGroupParams = {
  sceneIds?: number[];
};

export type ScenarioHealthGetReportParams = {
  refresh?: boolean;
};

export type ScenariosSearchScenariosParams = {
  /**
   * Optional free-text match against scenario name, description, or organization name.
   */
  query?: string;
  /**
   * Optional scenario language filter.
   */
  langCode?: string;
  /**
   * Number of matching scenarios to skip.
   */
  skip?: number;
  /**
   * Maximum number of scenarios to return.
   */
  take?: number;
  /**
   * Optional folder id, as the current user's organization sees it.
   */
  folderId?: number;
  /**
   * Optional exact folder name.
   */
  folderName?: string;
  /**
   * Optional filter on whether the AI conversation is on.
   */
  isLlmEnabled?: boolean;
  /**
   * Optional filter on published (true) or draft (false).
   */
  isPublished?: boolean;
  /**
   * Only scenarios the current user created.
   */
  createdByMe?: boolean;
  /**
   * Optional match against the names of the scenario's personas.
   */
  personaName?: string;
  /**
   * Optional match against the names of the roles the scenario's personas play.
   */
  roleName?: string;
  /**
   * Optional match against the scenario's context facts and intro text.
   */
  containsText?: string;
};

export type ScenariosUpdateScenarioPdfBodyOne = {
  pdfFile?: Blob | File;
};

export type ScenariosUpdateScenarioPdfBodyTwo = {
  pdfFile?: Blob;
};

export type ScheduledCallsGetParams = {
  skip?: number;
  take?: number;
};

export type ScheduledCallsUpdateStatusParams = {
  status?: ScheduledCallStatus;
};

export type SpeechSynthesizeSpeechStreamParams = {
  format?: string;
};

export type SpeechRecognizeSpeechParams = {
  lng?: string;
};

export type SpeechRecognizeSpeechBodyOne = {
  file?: Blob | File;
};

export type SpeechRecognizeSpeechBodyTwo = {
  file?: Blob;
};

export type UserGroupGetParams = {
  skip?: number;
  take?: number;
};
